import React, { useState, useRef, useEffect, useCallback, useMemo, forwardRef, useImperativeHandle } from 'react';
import * as Tone from 'tone';
import { parseMidiFile } from '../utils/midiFileParser';
import { ParsedMidiFile, MidiPlaybackState } from '../utils/midiTypes';
import { MidiEventMerger } from '../utils/midiEventMerger';
import { ChordRegion, MidiAnalysis, fileKeyOf, fetchMidiAnalysis, analysisErrorMessage, needsName, nameVoicings, rememberNames, withName, mergeRegions, adjacentRange, selectionAfterClick, splitRegion, regionAt, shownSegment, SpellingKey, cachedSpellingMode, voicingKey, keyAreasFromAnalysis, keysByRegion, keyRuns, keyAreaId, regionRefs, isEnabled, regionVoicing, amendmentsFor, withAmendments } from '../utils/midiAnalysis';
import { ChordSegment } from '../utils/chordChartExport';
import { KeyCenter, KeyCenterRegion, keyCentersEqual, reconcileRegions, clearKeyCenterAtChord, setKeyCenterAtChord, setNoKeyCenterAtChord, regionCoveringChord } from '../utils/keyCenterRegions';
import MidiZoomView from './MidiZoomView';
import KeyCenterPicker, { PickerAnchor } from './KeyCenterPicker';
import { KeyMarker } from './MidiZoomView';
import FollowToggle from './FollowToggle';
import { DatedEntry, DivergentKey, FileKeys, RegionChange, follows, holdsNotesOf, isCharted, linkedTo } from '../utils/chartFileLink';

export interface MidiControllerRef {
    cancelProcessing: () => void;
    hasFile: () => boolean;
    togglePlayback: () => void;
    // Whether the file is playing. Current within the render that starts or
    // stops it: the handle is set before the parent's effects run.
    isPlaying: () => boolean;
    // Shift+V: shows or hides the selected regions (see toggleSelectedEnabled)
    toggleSelectedEnabled: () => void;
    // Region edit mode, which Enter and Escape end, and during which a key
    // pressed over a selected chart chord must not clear the toggled notes
    isEditingRegion: () => boolean;
    stopEditingRegion: () => void;
    // Follow: the playhead (and the zoomed view's cue) to a chart chord's
    // time, silently, selecting its region. Nothing is sent or put on the
    // keys: the chart has already put its chord there.
    seekSilently: (seconds: number) => void;
    // The file and the start of the chord region at the playhead, which date
    // a chord added from the file's notes; null with no regions
    fileSourceAt: () => { fileKey: string; fileName: string; seconds: number } | null;
}

interface MidiControllerProps {
    midiEventMerger: MidiEventMerger | null;
    // keepSounding: notes to leave ringing rather than release (see App.tsx)
    clearAllNotes: (keepSounding?: number[]) => void;
    onSustainStateChange: (sustainState: boolean) => void;
    onScrubbingStateChange: (isScrubbing: boolean) => void;
    onPausedStateChange: (isPaused: boolean) => void;
    // The user scrubbed, played, navigated or selected regions: the file is
    // about to put its notes on the keys. The chart keeps its selection (it
    // is where promoted chords go), but leaves any edit mode, so the file's
    // notes never land in a chord being edited.
    onFileInteraction: () => void;
    // The promote button: these regions (the selected ones, in order) go to
    // the chart as chords, dated to this file
    onPromoteRegions: (fileKey: string, fileName: string, regions: ChordRegion[]) => void;
    // Each committed change to the regions, so the chart's chords dated to
    // this file follow it (chartFileLink.ts), with every region as it is now.
    // Held while Follow is off, then sent as one report when it is back on
    // (`afterUnlinked`), for App to ask about. `toChart`: regions split off a
    // charted one, to be charted beside it in the same edit. `chartKeeps`:
    // the report of undoing an edit taken from the chart, which the chart
    // takes as "Keep chart" does. Null file: none loaded any more.
    onRegionsSynced: (fileKey: string | null, changes: RegionChange[], regions: ChordRegion[], keys: FileKeys | null, toChart: ChordRegion[], afterUnlinked: boolean, chartKeeps: boolean) => void;
    // The regions as they are now, Follow or not (promotion places by them)
    onFileRegions: (fileKey: string | null, regions: ChordRegion[]) => void;
    // App is asking about a report: later ones wait, as while unlinked
    reportsHeld: boolean;
    // The chart's own keys on chords dated to this file that differ from the
    // file's: a marker on the key lane offers to settle each either way
    divergentChartKeys: DivergentKey[];
    onReturnChartKey: (chordId: string) => void;
    // The chart's global key when it differs from the file's, the same way
    divergentChartGlobalKey: { keyCenter: KeyCenter | null } | null;
    onReturnChartGlobalKey: () => void;
    // The chart's chords dated to any MIDI file: which regions are charted,
    // and which charted ones the chart has changed
    chartChords: DatedEntry[];
    // A region's marker: its chart chords take its notes and name
    onUpdateChartFromRegion: (fileKey: string, region: ChordRegion) => void;
    // A file's analysis has arrived: App makes a chart from it, or asks. Its
    // keys come with it, since the report carrying them is handled against
    // the chart as it was before the chart was made
    onFileAnalyzed: (fileKey: string, fileName: string, regions: ChordRegion[], keys: FileKeys) => void;
    soundOn: boolean;
    setSoundOn: (soundOn: boolean) => void;
    audioInitialized: boolean;
    setAudioInitialized: (initialized: boolean) => void;
    // MIDI Input/Output management for unified device menu
    midiAccess: WebMidi.MIDIAccess | null;
    selectedInputId: string | null;
    setSelectedInputId: (id: string | null) => void;
    // Note state management for pause-to-toggled functionality
    activeNotes: number[];
    toggledNotes: number[];
    heldNotes: number[];
    setActiveNotes: (notes: number[]) => void;
    setToggledNotes: (notes: number[]) => void;
    setHeldNotes: (notes: number[]) => void;
    // The /sentiserver URL, and the READ / FEEL setting that picks which of a
    // chord region's two names the pins show
    server: string;
    notationLegible: boolean;
    // A chord symbol drawn as the app draws them (App.tsx assembleChordSymbol),
    // and a note name, both spelled in `keyCenter`, or by the chord's own root
    // and mode when it is null, as the Chord Symbol panel spells.
    // `spellingMode` is the mode of the chord's tonic scale in that key
    // (undefined: the key's own mode).
    chordSymbol: (segment: ChordSegment, keyCenter: KeyCenter | null, spellingMode?: number) => JSX.Element;
    // The root alone, spelled as chordSymbol spells it
    chordRoot: (segment: ChordSegment, keyCenter: KeyCenter | null, spellingMode?: number) => string;
    noteName: (pitchClass: number, keyCenter: KeyCenter | null) => string;
    // The user's own key lock (null when none is), which overrides the
    // file's keys, and the mode borrowing setting: in a key, regions are
    // spelled by the tonic scale their notes imply in it, which the server
    // works out
    lockedTonicNote: number | null;
    lockedTonicMode: number;
    preferModeBorrowing: boolean;
    // The file's key at the playhead (its key area, or its global key), for
    // App's document lock; null with no file or no key
    onFileKeyAt: (keyCenter: KeyCenter | null) => void;
    // Strikes a chord's notes as the chord chart does (rolled, strummed or
    // arpeggiated by instrument and settings)
    onPlayChordNotes: (notes: number[]) => void;
    // Follow (FollowToggle): the chart and the file share one place. While
    // on, the player reports the chart timestamp the file's place has reached
    // (the latest at or before it, so App re-renders only when one is
    // crossed), selects the region a chart chord came from when the chart
    // seeks it, and does not play while the chart is playing.
    followOn: boolean;
    onToggleFollow: () => void;
    onFollowTime: (fileKey: string | null, seconds: number | null) => void;
    // Follow off: the selected chart chord's time in this file, its region
    // outlined dashed, so the chart's place shows without moving the file's
    chartSelectionSeconds: number | null;
    playLocked: boolean;
}

// Scrubbing can land on a moment where dozens of notes are sounding at once -
// the sustain-extended data does that by design, and it's musically correct -
// but firing them all at written velocity sums into a wall. Past a full
// pedaled chord's worth of notes, trim velocity by batch size. Every note
// still sounds; the block just doesn't keep getting louder without limit.
//
// TRIM_EXPONENT 0.5 would hold total loudness exactly constant (uncorrelated
// sources sum in power, so amplitude grows with the square root of the count).
// A lower exponent compensates only partly, so a dense moment still reads as
// denser and louder than a sparse one - just not 8x louder. Tune by ear.
const BATCH_TRIM_THRESHOLD = 4;
const TRIM_EXPONENT = 0.5;
const MIN_BATCH_TRIM = 0.4;

const getBatchVelocityTrim = (batchSize: number): number => {
    if (batchSize <= BATCH_TRIM_THRESHOLD) return 1;
    return Math.max(MIN_BATCH_TRIM, Math.pow(BATCH_TRIM_THRESHOLD / batchSize, TRIM_EXPONENT));
};

const trimVelocity = (velocity: number, trim: number): number => {
    if (trim === 1) return velocity;
    return Math.max(1, Math.round(velocity * trim));
};

// The least time between two requests naming chord regions
const NAMING_INTERVAL_MS = 250;

const MidiController = forwardRef<MidiControllerRef, MidiControllerProps>(({ midiEventMerger, clearAllNotes, onSustainStateChange, onScrubbingStateChange, onPausedStateChange, onFileInteraction, onPromoteRegions, onRegionsSynced, onFileRegions, reportsHeld, divergentChartKeys, onReturnChartKey, divergentChartGlobalKey, onReturnChartGlobalKey, chartChords, onUpdateChartFromRegion, onFileAnalyzed, soundOn, setSoundOn, audioInitialized, setAudioInitialized, midiAccess, selectedInputId, setSelectedInputId, activeNotes, toggledNotes, heldNotes, setActiveNotes, setToggledNotes, setHeldNotes, server, notationLegible, chordSymbol, chordRoot, noteName, onFileKeyAt, onPlayChordNotes, followOn, onToggleFollow, onFollowTime, chartSelectionSeconds, playLocked, lockedTonicNote, lockedTonicMode, preferModeBorrowing }, ref) => {
    const [parsedMidi, setParsedMidi] = useState<ParsedMidiFile | null>(null);
    const [playbackState, setPlaybackState] = useState<MidiPlaybackState>({
        isPlaying: false,
        currentTime: 0,
        duration: 0,
        playbackSpeed: 1.0,
        isLooping: false,
        isScrubbing: false
    });
    const isPlayingRef = useRef(false);

    // Sync isPlayingRef with playbackState.isPlaying
    useEffect(() => {
        isPlayingRef.current = playbackState.isPlaying;
    }, [playbackState.isPlaying]);

    const scheduledEventsRef = useRef<number[]>([]);

    const [sustainTimeline, setSustainTimeline] = useState<Array<{ time: number, state: boolean }>>([]);
    const [enhancedNotes, setEnhancedNotes] = useState<Array<any>>([]);
    const [isReadingMidi, setIsReadingMidi] = useState(false);
    const [isComputingNav, setIsComputingNav] = useState(false);
    const processingCancelRef = useRef(false);
    const processingSessionRef = useRef(0);

    // Chord regions and key areas from the backend (utils/midiAnalysis.ts).
    // `regions` starts as the analysis's own and is what pin drags edit.
    const [fileKey, setFileKey] = useState<string | null>(null);
    const [midiAnalysis, setMidiAnalysis] = useState<MidiAnalysis | null>(null);
    const [regions, setRegions] = useState<ChordRegion[]>([]);
    const [isAnalyzingChords, setIsAnalyzingChords] = useState(false);
    const [analysisError, setAnalysisError] = useState<string | null>(null);
    // Zoomed in: chord region edit mode (MidiZoomView) takes over the panel
    const [isZoomed, setIsZoomed] = useState(false);
    // Where play starts from in the zoomed view: the last place its roll was
    // clicked, and the playhead's place on zooming in
    const [cue, setCue] = useState(0);
    // The selected regions, by their backend indices in region order, and the
    // one a shift-click selects from (the last plain or ⌘ click)
    const [selectedRegions, setSelectedRegions] = useState<number[]>([]);
    const [selectionAnchor, setSelectionAnchor] = useState<number | null>(null);
    // Region edit mode: the one region whose notes the piano's toggled notes
    // are correcting, as a chart chord's edit mode takes them
    const [editingRegion, setEditingRegion] = useState<number | null>(null);
    // The file's key centers, kept as the chart keeps its own: a global key,
    // and key areas anchored to region starts ('r' + index, keyAreaId), so
    // utils/keyCenterRegions.ts applies to them unchanged and an area moves
    // with its region's start. `auto` from the analysis; `manual` once the
    // user sets one, which re-analysis must never replace.
    const [keyAreas, setKeyAreas] = useState<KeyCenterRegion[]>([]);
    const [fileGlobalKey, setFileGlobalKey] = useState<KeyCenter | null>(null);
    // Whether the analysis's fast key areas (Giant Steps) are in use: offered,
    // never switched to on its own
    const [useFastKeyAreas, setUseFastKeyAreas] = useState(false);
    // Undo / redo for region edits: the regions and key areas before each
    // edit, and after each undone one. A drag is one edit however far it went.
    // `fromChart`: the edit took the chart's chord or key, settling a marker
    // the chart's way, so undoing it brings the difference back rather than
    // the chart following the file (`chartKeepsRef`)
    type RegionSnapshot = { regions: ChordRegion[]; keyAreas: KeyCenterRegion[]; globalKey: KeyCenter | null; fromChart?: boolean };
    const [regionHistory, setRegionHistory] = useState<{ past: RegionSnapshot[]; future: RegionSnapshot[] }>({ past: [], future: [] });

    const clearFileAnalysis = () => {
        setIsZoomed(false);
        setSelectedRegions([]);
        setSelectionAnchor(null);
        setEditingRegion(null);
        setRegionHistory({ past: [], future: [] });
        setKeyAreas([]);
        setFileGlobalKey(null);
        setUseFastKeyAreas(false);
        setFileKey(null);
        setMidiAnalysis(null);
        setRegions([]);
        setIsAnalyzingChords(false);
        setAnalysisError(null);
    };

    // Scrubbing state management
    const previousScrubTimeRef = useRef<number>(0);
    const scrubbingActiveNotesRef = useRef<Set<number>>(new Set());
    const scrubbingActiveEventIdsRef = useRef<Set<string>>(new Set());
    const scrubbingSustainStateRef = useRef<boolean>(false);
    const progressBarRef = useRef<HTMLDivElement>(null);

    const [showSpeedMenu, setShowSpeedMenu] = useState(false);
    const [showMidiDeviceMenu, setShowMidiDeviceMenu] = useState(false);
    const speedOptions = [3, 2, 1.5, 1, 0.7, 0.5, 0.3, 0.2];

    // MIDI Output state
    const [midiOutputs, setMidiOutputs] = useState<WebMidi.MIDIOutput[]>([]);
    const [selectedOutputId, setSelectedOutputId] = useState<string | null>(null);
    const [midiOutputEnabled, setMidiOutputEnabled] = useState<boolean>(false);
    const selectedOutputRef = useRef<WebMidi.MIDIOutput | null>(null);

    const [processingProgress, setProcessingProgress] = useState(0);
    const [isDragOver, setIsDragOver] = useState(false);

    // Helper functions for MIDI device conflict detection
    const hasInputOutputConflict = useCallback(() => {
        // Check if "All MIDI devices" is selected while output is enabled AND an actual output device is selected
        if (midiOutputEnabled && selectedOutputId && selectedOutputId !== "no-device" && (selectedInputId === "" || selectedInputId === null)) {
            return true;
        }
        // Check if same device NAME is selected for both input and output
        if (selectedInputId && selectedOutputId && midiAccess) {
            const selectedInput = Array.from(midiAccess.inputs.values()).find(input => input.id === selectedInputId);
            const selectedOutput = midiOutputs.find(output => output.id === selectedOutputId);
            if (selectedInput && selectedOutput && selectedInput.name === selectedOutput.name) {
                return true;
            }
        }
        return false;
    }, [midiOutputEnabled, selectedInputId, selectedOutputId, midiAccess, midiOutputs]);

    const getDisplayText = useCallback(() => {
        if (hasInputOutputConflict()) {
            return "Fix MIDI input/output conflict";
        }
        const selectedOutput = midiOutputs.find(out => out.id === selectedOutputId);
        return selectedOutput ? ` ${selectedOutput.name}` : " Select input/output MIDI devices";
    }, [hasInputOutputConflict, midiOutputs, selectedOutputId]);

    // Initialize MIDI outputs
    useEffect(() => {
        if (navigator.requestMIDIAccess) {
            navigator.requestMIDIAccess().then(midiAccess => {
                const outputs = Array.from(midiAccess.outputs.values());
                setMidiOutputs(outputs);

                // Default to "no-device" if no output is selected
                if (!selectedOutputId) {
                    setSelectedOutputId("no-device");
                }
            }).catch(err => {
                console.error('MIDI access failed:', err);
            });
        }
    }, [selectedOutputId]);

    // Cleanup function for MIDI output device
    const cleanupMidiOutput = useCallback((device: WebMidi.MIDIOutput | null) => {
        if (device) {
            try {
                // Send "All Notes Off" (CC 123) and "All Sound Off" (CC 120) on all channels
                for (let channel = 0; channel < 16; channel++) {
                    device.send([176 + channel, 123, 0]); // All Notes Off
                    device.send([176 + channel, 120, 0]); // All Sound Off
                    device.send([176 + channel, 64, 0]);  // Sustain Off
                }
            } catch (error) {
                console.error('Error sending MIDI cleanup:', error);
            }
        }
    }, []);

    // Update selected output reference and cleanup when changed
    useEffect(() => {
        // Cleanup previous device before switching
        if (selectedOutputRef.current && midiOutputEnabled) {
            cleanupMidiOutput(selectedOutputRef.current);
        }

        // Set new device reference
        if (selectedOutputId) {
            const output = midiOutputs.find(out => out.id === selectedOutputId);
            selectedOutputRef.current = output || null;
        } else {
            selectedOutputRef.current = null;
        }
    }, [selectedOutputId, midiOutputs, midiOutputEnabled, cleanupMidiOutput]);

    // Cleanup MIDI output when disabled
    useEffect(() => {
        if (!midiOutputEnabled) {
            cleanupMidiOutput(selectedOutputRef.current);
        }
    }, [midiOutputEnabled, cleanupMidiOutput]);

    // Send MIDI data to output device
    const sendToMidiOutput = useCallback((midiData: number[]) => {
        if (midiOutputEnabled && selectedOutputRef.current) {
            try {
                selectedOutputRef.current.send(midiData);
            } catch (error) {
                console.error('Error sending MIDI output:', error);
            }
        }
    }, [midiOutputEnabled]);

    // State for precomputed MIDI events
    const [precomputedEvents, setPrecomputedEvents] = useState<Array<{
        time: number;
        data: number[];
        eventId: string;
        isNoteOn: boolean;
        isPrecomputed: boolean;
    }>>([]);

    // Pre-compute sustain timeline and note durations
    const preComputeFileData = useCallback(async (parsedMidi: ParsedMidiFile) => {
        const mySession = processingSessionRef.current;
        if (processingCancelRef.current || mySession !== processingSessionRef.current) return;
        // Create sustain timeline
        const sustainEvents = parsedMidi.controlChanges
            .filter(cc => cc.controller === 64)
            .sort((a, b) => a.time - b.time);

        const timeline: Array<{ time: number, state: boolean }> = [];
        let currentState = false;

        sustainEvents.forEach(event => {
            const newState = event.value >= 64;
            if (newState !== currentState) {
                (timeline as Array<{ time: number, state: boolean }>).push({
                    time: event.time,
                    state: newState
                });
                currentState = newState;
            }
        });

        if (mySession !== processingSessionRef.current) return;
        setSustainTimeline(timeline);

        const sustainAt = (t: number) => {
            let s = false;
            for (const ev of timeline) {
                if (ev.time <= t) s = ev.state; else break;
            }
            return s;
        };

        // Phase 0: Parse/enhance notes (0% -> 10%)
        const enhancedLocal: any[] = [];
        const totalNotes = parsedMidi.notes.length;
        for (let i = 0; i < parsedMidi.notes.length; i++) {
            if (processingCancelRef.current || mySession !== processingSessionRef.current) { setProcessingProgress(0); return; }
            const note = parsedMidi.notes[i];
            let effectiveEndTime = note.time + note.duration;
            const naturalEndTime = effectiveEndTime;
            let sustainActiveAtNoteEnd = false;
            let sustainReleaseTime = naturalEndTime;
            for (let j = sustainEvents.length - 1; j >= 0; j--) {
                if (processingCancelRef.current || mySession !== processingSessionRef.current) { setProcessingProgress(0); return; }
                const sustainEvent = sustainEvents[j];
                if (sustainEvent.time <= naturalEndTime) {
                    if (sustainEvent.value >= 64) {
                        sustainActiveAtNoteEnd = true;
                        const release = sustainEvents.find(s => s.time > sustainEvent.time && s.value < 64);
                        if (release) sustainReleaseTime = release.time;
                    }
                    break;
                }
            }
            if (sustainActiveAtNoteEnd && sustainReleaseTime > naturalEndTime) {
                effectiveEndTime = sustainReleaseTime;
            }
            enhancedLocal.push({ ...note, effectiveEndTime });
            if (i % 50 === 0) {
                const frac = i / Math.max(1, totalNotes);
                const pct = Math.round(0 + (10 - 0) * frac);
                setProcessingProgress(pct);
                await new Promise(resolve => setTimeout(resolve, 0));
                if (processingCancelRef.current || mySession !== processingSessionRef.current) { setProcessingProgress(0); return; }
            }
        }
        if (mySession !== processingSessionRef.current) return;
        setEnhancedNotes(enhancedLocal);

        // Create precomputed MIDI events with sustain effects baked in
        const precomputed: Array<{ time: number; data: number[]; eventId: string; isNoteOn: boolean; isPrecomputed: boolean; }> = [];
        const allEvents: Array<{ time: number; type: 'noteOn' | 'noteOff'; note: number; velocity: number; originalNoteIndex: number; }> = [];

        // Debug: Check what notes exist in a specific time range where fast melodic notes should be
        const debugTimeStart = 14.98; // Repeated notes should be in this range
        const debugTimeEnd = 15.68;
        const notesInRange = parsedMidi.notes.filter(note => note.time >= debugTimeStart && note.time <= debugTimeEnd);
        console.log(`DEBUG: Found ${notesInRange.length} parsed MIDI notes in range ${debugTimeStart}-${debugTimeEnd}:`);
        notesInRange.forEach(note => {
            console.log(`  Note ${note.note} at time ${note.time.toFixed(6)}, duration ${note.duration.toFixed(6)}`);
        });

        for (let index = 0; index < parsedMidi.notes.length; index++) {
            if (processingCancelRef.current || mySession !== processingSessionRef.current) { setProcessingProgress(0); return; }
            const note = parsedMidi.notes[index];
            allEvents.push({ time: note.time, type: 'noteOn', note: note.note, velocity: note.velocity, originalNoteIndex: index });
            allEvents.push({ time: note.time + note.duration, type: 'noteOff', note: note.note, velocity: note.velocity, originalNoteIndex: index });
        }
        allEvents.sort((a, b) => a.time - b.time);
        for (const event of allEvents) {
            if (processingCancelRef.current || mySession !== processingSessionRef.current) { setProcessingProgress(0); return; }
            const sustainAtEventTime = sustainAt(event.time);
            if (event.type === 'noteOn') {
                precomputed.push({ time: event.time, data: [144, event.note, event.velocity], eventId: `${event.note}-${event.time.toFixed(3)}`, isNoteOn: true, isPrecomputed: true });
            } else if (event.type === 'noteOff') {
                if (!sustainAtEventTime) {
                    precomputed.push({ time: event.time, data: [128, event.note, 0], eventId: `${event.note}-${event.originalNoteIndex}-off`, isNoteOn: false, isPrecomputed: true });
                }
            }
        }
        if (mySession !== processingSessionRef.current) return;
        precomputed.sort((a, b) => a.time - b.time);

        // Debug: Check what precomputed events were created in the same time range
        const precomputedInRange = precomputed.filter(event => event.time >= debugTimeStart && event.time <= debugTimeEnd);
        console.log(`DEBUG: Generated ${precomputedInRange.length} precomputed events in range ${debugTimeStart}-${debugTimeEnd}:`);
        precomputedInRange.forEach(event => {
            const noteNumber = event.data[1];
            const eventType = event.isNoteOn ? 'ON' : 'OFF';
            console.log(`  ${eventType} Note ${noteNumber} at time ${event.time.toFixed(6)}`);
        });

        setPrecomputedEvents(precomputed);
    }, []);

    // Fast sustain state lookup
    const getSustainStateAtTime = useCallback((time: number) => {
        let sustainState = false;

        for (const event of sustainTimeline) {
            if (event.time <= time) {
                sustainState = event.state;
            } else {
                break;
            }
        }

        return sustainState;
    }, [sustainTimeline]);


    // Chord regions and key areas for the file, from the backend. Runs beside
    // the player's own precompute rather than after it, so the pins arrive as
    // soon as the server answers; a newer file (or a cancel) discards a late
    // answer through the processing session.
    const analyzeChords = useCallback(async (file: File, mySession: number) => {
        clearFileAnalysis();
        setIsAnalyzingChords(true);
        try {
            const bytes = await file.arrayBuffer();
            const key = await fileKeyOf(bytes);
            if (mySession !== processingSessionRef.current) return;
            setFileKey(key);
            const result = await fetchMidiAnalysis(server, bytes, key);
            if (mySession !== processingSessionRef.current) return;
            rememberNames(result.regions);
            setMidiAnalysis(result);
            setRegions(result.regions);
            setKeyAreas(keyAreasFromAnalysis(result.key_areas, result.global_key));
            setFileGlobalKey(result.global_key);
        } catch (err) {
            if (mySession !== processingSessionRef.current) return;
            console.error('MIDI chord analysis failed:', err);
            setAnalysisError(analysisErrorMessage(err));
        } finally {
            if (mySession === processingSessionRef.current) setIsAnalyzingChords(false);
        }
    }, [server]);

    // Names regions that have none for the shown READ / FEEL setting: the two
    // either side of a dragged pin, and every region when the setting changes.
    // Throttled rather than debounced, so a pin re-names live while it is
    // still moving, at most every NAMING_INTERVAL_MS (/batch allows 5 a
    // second). An answer that arrives after the notes changed again is still
    // cached, and a region takes it only if its notes are the ones named.
    // Each region is spelled in its own key area's key (its tonic scale's
    // mode in that key, fetched with its name), or in the user's own lock,
    // which overrides the file's keys as it does the chart's
    const keyByRegion = useMemo(() => keysByRegion(regions, keyAreas, fileGlobalKey), [regions, keyAreas, fileGlobalKey]);
    const keyFor = useCallback((region: ChordRegion): KeyCenter | null => {
        if (lockedTonicNote !== null) return { pitchClass: lockedTonicNote, mode: lockedTonicMode === 1 ? 1 : 0 };
        const key = keyByRegion.get(region.index);
        return key === undefined ? null : key;
    }, [lockedTonicNote, lockedTonicMode, keyByRegion]);
    const spellingFor = useCallback((region: ChordRegion): SpellingKey | null => {
        const key = keyFor(region);
        return key === null ? null : { keyCenter: key.pitchClass, keyMode: key.mode, preferModeBorrowing };
    }, [keyFor, preferModeBorrowing]);
    // Bumped when spelling modes arrive, which live in a cache, not in state
    const [spellingVersion, setSpellingVersion] = useState(0);

    const lastNamingAtRef = useRef(0);
    useEffect(() => {
        const wanted = regions.filter(r => {
            if (needsName(r, notationLegible)) return true;
            const spelling = spellingFor(r);
            const notes = regionVoicing(r);
            return spelling !== null && notes.length >= 2
                && cachedSpellingMode(voicingKey(notes), notationLegible, spelling) === undefined;
        });
        if (wanted.length === 0) return;
        // One request per key, since a request carries one
        const groups = new Map<string, { spelling: SpellingKey | null; regions: ChordRegion[] }>();
        wanted.forEach(r => {
            const spelling = spellingFor(r);
            const key = spelling === null ? 'none' : `${spelling.keyCenter}:${spelling.keyMode}`;
            const group = groups.get(key);
            if (group) group.regions.push(r); else groups.set(key, { spelling, regions: [r] });
        });
        const wait = Math.max(0, lastNamingAtRef.current + NAMING_INTERVAL_MS - Date.now());
        let cancelled = false;
        const timer = setTimeout(async () => {
            lastNamingAtRef.current = Date.now();
            for (const { spelling, regions: group } of Array.from(groups.values())) {
                if (cancelled) return;
                try {
                    const names = await nameVoicings(server, group.map(r => regionVoicing(r)), notationLegible, spelling);
                    const byIndex = new Map(group.map((r, i) => [r.index, { notesKey: voicingKey(regionVoicing(r)), name: names[i] }]));
                    setRegions(prev => prev.map(r => {
                        const named = byIndex.get(r.index);
                        return named && named.notesKey === voicingKey(regionVoicing(r)) && needsName(r, notationLegible)
                            ? withName(r, named.name, notationLegible) : r;
                    }));
                    if (spelling !== null) setSpellingVersion(v => v + 1);
                } catch (err) {
                    console.error('Naming chord regions failed:', err);
                }
            }
        }, wait);
        return () => { cancelled = true; clearTimeout(timer); };
    }, [regions, notationLegible, server, spellingFor]);

    // A region's symbol for the zoomed view, spelled in its key (keyFor) in
    // its own tonic scale's mode; `notesKey` is the notes its name was given for
    const regionSymbol = useCallback((region: ChordRegion, segment: ChordSegment, notesKey: string) => {
        const spelling = spellingFor(region);
        return chordSymbol(segment, keyFor(region), spelling === null ? undefined : cachedSpellingMode(notesKey, notationLegible, spelling));
        // spellingVersion: a fetched spelling mode re-draws the symbols
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [chordSymbol, keyFor, spellingFor, notationLegible, spellingVersion]);

    // Its root alone, spelled the same way, for a band too narrow for the symbol
    const regionRoot = useCallback((region: ChordRegion, segment: ChordSegment, notesKey: string) => {
        const spelling = spellingFor(region);
        return chordRoot(segment, keyFor(region), spelling === null ? undefined : cachedSpellingMode(notesKey, notationLegible, spelling));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [chordRoot, keyFor, spellingFor, notationLegible, spellingVersion]);

    // The file's key at the playhead, for App's document lock: the key of the
    // region there, or the global key before the first. Reported when it
    // changes, not at every tick of playback.
    const lastKeyAtRef = useRef<KeyCenter | null | undefined>(undefined);
    useEffect(() => {
        let key: KeyCenter | null = null;
        if (fileKey && regions.length > 0) {
            const k = regionAt(regions, playbackState.currentTime);
            const regionKey = k >= 0 ? keyByRegion.get(regions[k].index) : undefined;
            key = k >= 0 ? (regionKey === undefined ? null : regionKey) : fileGlobalKey;
        }
        const last = lastKeyAtRef.current;
        const same = last !== undefined && keyCentersEqual(last, key)
            && (last === null || key === null || last.spelling === key.spelling);
        if (same) return;
        lastKeyAtRef.current = key;
        onFileKeyAt(key);
        // onFileKeyAt is App's latest whenever this runs
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [playbackState.currentTime, regions, keyByRegion, fileGlobalKey, fileKey]);

    // The key lane's stretches, each auto one with the analysis's confidence
    const keyLane = useMemo(() => {
        const source = midiAnalysis ? (useFastKeyAreas && midiAnalysis.fast_key_areas ? midiAnalysis.fast_key_areas : midiAnalysis.key_areas) : [];
        const confidence = new Map(source.map(area => [area.region_index, area.confidence]));
        return keyRuns(regions, keyAreas, fileGlobalKey).map(run => ({ ...run, confidence: confidence.get(run.regionIndex) }));
    }, [regions, keyAreas, fileGlobalKey, midiAnalysis, useFastKeyAreas]);

    // Setting a key from a region, with the chart's own picker
    // The picker sets the key from one region, or the file's global key (regionIndex null)
    const [keyPicker, setKeyPicker] = useState<{ regionIndex: number | null; anchor: PickerAnchor } | null>(null);
    const pickKeyAt = (keyCenter: KeyCenter, keepOpen?: boolean) => {
        if (keyPicker === null) return;
        recordRegionEdit(regions);
        if (keyPicker.regionIndex === null) setFileGlobalKey(keyCenter);
        else setKeyAreas(setKeyCenterAtChord(regionRefs(regions), keyAreas, keyAreaId(keyPicker.regionIndex), keyCenter));
        if (!keepOpen) setKeyPicker(null);
    };
    // A region's picker shows the local key in force there, if any: where the
    // global key shows through, no key is picked yet
    const keyPickerLocalKey = keyPicker !== null && keyPicker.regionIndex !== null ? (() => {
        const covering = regionCoveringChord(regionRefs(regions), keyAreas, keyAreaId(keyPicker.regionIndex));
        return covering === null ? null : covering.keyCenter;
    })() : null;
    // The key area starting at the picker's region, if any
    const keyPickerStartingHere = keyPicker !== null && keyPicker.regionIndex !== null
        ? keyAreas.find(area => area.startChordId === keyAreaId(keyPicker.regionIndex!))
        : undefined;
    // "Use global key" set at this region itself, not earlier: the picker
    // shows that choice
    const keyPickerUsesGlobal = keyPickerStartingHere !== undefined && keyPickerStartingHere.keyCenter === null;
    // In force by a "use global key" marker, set here or earlier: the picker
    // shows the global key it gives
    const keyPickerInGlobal = keyPicker !== null && keyPicker.regionIndex !== null && (() => {
        const covering = regionCoveringChord(regionRefs(regions), keyAreas, keyAreaId(keyPicker.regionIndex!));
        return covering !== null && covering.keyCenter === null;
    })();
    // "Use global key": from this region on the global key applies, a marker
    // that stays. Offered on any region.
    const keyPickerCanUseGlobal = keyPicker !== null && keyPicker.regionIndex !== null;
    const useGlobalKeyAt = () => {
        if (keyPicker === null || keyPicker.regionIndex === null) return;
        recordRegionEdit(regions);
        setKeyAreas(setNoKeyCenterAtChord(regionRefs(regions), keyAreas, keyAreaId(keyPicker.regionIndex)));
        setKeyPicker(null);
    };
    // "Clear": the key change starting at the region goes and the key before
    // it carries on, the analysis's own too (the user is correcting it); on
    // the global key, no global key
    const keyPickerCanClear = keyPicker !== null
        && (keyPicker.regionIndex === null ? fileGlobalKey !== null : keyPickerStartingHere !== undefined);
    const clearKeyAt = () => {
        if (keyPicker === null) return;
        recordRegionEdit(regions);
        if (keyPicker.regionIndex === null) setFileGlobalKey(null);
        else setKeyAreas(clearKeyCenterAtChord(regionRefs(regions), keyAreas, keyAreaId(keyPicker.regionIndex)));
        setKeyPicker(null);
    };

    // The analysis's fast key areas in place of its usual ones, or back: the
    // auto layer is replaced, the user's own keys kept
    const toggleFastKeyAreas = () => {
        if (!midiAnalysis || !midiAnalysis.fast_key_areas) return;
        const manual = keyAreas.filter(area => area.source === 'manual');
        const auto = keyAreasFromAnalysis(useFastKeyAreas ? midiAnalysis.key_areas : midiAnalysis.fast_key_areas, fileGlobalKey)
            .filter(area => !manual.some(m => m.startChordId === area.startChordId));
        const position = new Map(regions.map((r, k) => [keyAreaId(r.index), k]));
        const next = [...manual, ...auto]
            .filter(area => position.has(area.startChordId))
            .sort((a, b) => position.get(a.startChordId)! - position.get(b.startChordId)!);
        recordRegionEdit(regions);
        setKeyAreas(next);
        setUseFastKeyAreas(!useFastKeyAreas);
    };

    // A note name in a region's key (a scale region's root)
    const regionNoteName = useCallback((region: ChordRegion, pitchClass: number) => noteName(pitchClass, keyFor(region)), [noteName, keyFor]);

    // An edit to the regions, recorded for undo against what it replaced
    const recordRegionEdit = useCallback((before: ChordRegion[], keyAreasBefore: KeyCenterRegion[] = keyAreas) => {
        setRegionHistory(prev => ({ past: [...prev.past, { regions: before, keyAreas: keyAreasBefore, globalKey: fileGlobalKey }], future: [] }));
    }, [keyAreas, fileGlobalKey]);
    // The same for an edit that takes the chart's chord or key into the file
    const recordEditFromChart = () => {
        setRegionHistory(prev => ({ past: [...prev.past, { regions, keyAreas, globalKey: fileGlobalKey, fromChart: true }], future: [] }));
    };
    // Set by undoing an edit from the chart: the report it makes is taken by
    // the chart as "Keep chart" is, so the chart keeps what it has and the
    // marker comes back
    const chartKeepsRef = useRef(false);

    // Merge: two or more selected regions side by side become the first of
    // them, which is then selected and sounded
    const mergeRange = adjacentRange(regions, selectedRegions);

    // Promote: the selected regions not yet in the chart, in order. App leaves
    // out any with no chord to chart; the button needs at least one that has one.
    const selectedInOrder = regions.filter(r => selectedRegions.includes(r.index));
    const toPromote = fileKey ? selectedInOrder.filter(r => isEnabled(r) && !isCharted(chartChords, fileKey, r)) : [];
    const promotableCount = toPromote.filter(r => !r.is_scale && !!shownSegment(r, notationLegible)).length;
    const canPromote = !!parsedMidi && promotableCount > 0;
    const promoteTitle = (() => {
        if (canPromote) return `Add ${promotableCount} chord region${promotableCount === 1 ? '' : 's'} to the chord chart`;
        if (selectedInOrder.length > 0 && selectedInOrder.every(r => !isEnabled(r))) return 'Hidden regions are not added to the chord chart';
        if (selectedInOrder.length > 0 && toPromote.length === 0) return 'Already in the chord chart';
        return 'Add the selected chord regions to the chord chart';
    })();
    const promoteSelectedRegions = () => {
        if (!canPromote || !fileKey || !parsedMidi) return;
        onPromoteRegions(fileKey, parsedMidi.name || "MIDI file", toPromote);
    };

    // The regions whose chart chords hold other notes now, each with those
    // chords: only the user can say which is right, and when two chart chords
    // are dated to one region, which of them the region should take
    const divergedRegions = fileKey
        ? regions
            .map(r => ({ index: r.index, chords: chartChords.filter(entry => linkedTo(entry, fileKey, r) && !holdsNotesOf(entry, r)).map(entry => ({ id: entry.id, name: entry.name })) }))
            .filter(d => d.chords.length > 0)
        : [];
    // The regions that could be added to the chart and are not in it: each
    // shows a ⊕ that adds it
    const unchartedRegions = fileKey && parsedMidi
        ? regions.filter(r => isEnabled(r) && !r.is_scale && !!shownSegment(r, notationLegible) && !isCharted(chartChords, fileKey, r)).map(r => r.index)
        : [];
    const promoteRegion = (index: number) => {
        const region = regions.find(r => r.index === index);
        if (fileKey && parsedMidi && region) onPromoteRegions(fileKey, parsedMidi.name || "MIDI file", [region]);
    };
    const updateChartFromRegion = (index: number) => {
        const region = regions.find(r => r.index === index);
        if (fileKey && region) onUpdateChartFromRegion(fileKey, region);
    };

    // The file's own keys, the user's lock aside, as the chart takes them:
    // at each region the key area in force, or null where the global key
    // applies (before any area, or after a "use global key"), and where that
    // area starts, so a marker reaches the chart too
    const fileKeysOf = (key: string): FileKeys => {
        const byAnchor = new Map(keyAreas.map(area => [area.startChordId, area]));
        let inForce: KeyCenter | null = null;
        let changeStart: number | null = null;
        return {
            fileKey: key,
            globalKey: fileGlobalKey,
            regionKeys: regions.map(r => {
                const area = byAnchor.get(keyAreaId(r.index));
                if (area) {
                    inForce = area.keyCenter;
                    changeStart = r.start;
                }
                return { start: r.start, keyCenter: inForce, changeStart };
            }),
        };
    };

    // Each analysis is announced once, when the file it is for has been read
    // (the two run side by side), from an effect so App's latest handler hears it
    const announcedAnalysisRef = useRef<MidiAnalysis | null>(null);
    useEffect(() => {
        if (!midiAnalysis || !fileKey || !parsedMidi || announcedAnalysisRef.current === midiAnalysis) return;
        announcedAnalysisRef.current = midiAnalysis;
        onFileAnalyzed(fileKey, parsedMidi.name || 'MIDI file', regions, fileKeysOf(fileKey));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [midiAnalysis, fileKey, parsedMidi]);

    // Reports each committed change to the regions, by index, against the
    // copy last reported: not during a pin drag (a scrub), which reports once,
    // at its end, and a region only once it is named for the shown setting, so
    // its notes and name reach the chart together. The file's keys go with
    // every report, and alone when only they changed (the key lane). While
    // Follow is off nothing is reported after the file's first report, so the
    // changes pile up against what the chart last took, and go as one report
    // when Follow is back on.
    const reportedRegionsRef = useRef<Map<number, ChordRegion>>(new Map());
    const reportedKeysRef = useRef('');
    const heldWhileUnlinkedRef = useRef(false);
    // Regions split off a charted region, by index, to be charted when first
    // reported (once named), and hidden regions whose chart chord the hide
    // deleted, to be charted again when reported shown. Another file's indices
    // are not these regions.
    const splitToChartRef = useRef<Set<number>>(new Set());
    const hiddenFromChartRef = useRef<Set<number>>(new Set());
    useEffect(() => {
        splitToChartRef.current = new Set();
        hiddenFromChartRef.current = new Set();
    }, [fileKey]);
    // The chart the last report was sent against: Follow waits for App's
    // answer to it, rather than place the file by chords not yet moved
    const reportedAgainstRef = useRef<DatedEntry[] | null>(null);
    useEffect(() => {
        if (!playbackState.isScrubbing) onFileRegions(fileKey || null, regions);
        // onFileRegions is App's latest whenever this runs
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [fileKey, regions, playbackState.isScrubbing]);
    useEffect(() => {
        const reported = reportedRegionsRef.current;
        if (!fileKey) {
            if (reported.size > 0 || reportedKeysRef.current !== '') {
                reportedRegionsRef.current = new Map();
                reportedKeysRef.current = '';
                heldWhileUnlinkedRef.current = false;
                onRegionsSynced(null, [], [], null, [], false, false);
            }
            return;
        }
        if (playbackState.isScrubbing) return;
        // An undone edit from the chart. Held while unlinked, it goes with the
        // rest, which the chart is asked about when linked again.
        const chartKeeps = chartKeepsRef.current;
        chartKeepsRef.current = false;
        if ((!followOn || reportsHeld) && reported.size > 0) {
            heldWhileUnlinkedRef.current = true;
            return;
        }
        // Linked again: what piled up goes now, or there was nothing
        const afterUnlinked = heldWhileUnlinkedRef.current;
        heldWhileUnlinkedRef.current = false;
        const changes: RegionChange[] = [];
        const present = new Set<number>();
        regions.forEach(r => {
            present.add(r.index);
            if (needsName(r, notationLegible)) return;
            const before = reported.get(r.index);
            if (before === r) return;
            changes.push({ before, after: r });
            reported.set(r.index, r);
        });
        reported.forEach((r, index) => {
            if (present.has(index)) return;
            changes.push({ before: r });
            reported.delete(index);
        });
        const keys = fileKeysOf(fileKey);
        const keysJson = JSON.stringify(keys);
        if (changes.length === 0 && keysJson === reportedKeysRef.current) return;
        reportedKeysRef.current = keysJson;
        const toChart: ChordRegion[] = [];
        changes.forEach(c => {
            const { before, after } = c;
            if (!after) return;
            if (!before) {
                if (!splitToChartRef.current.has(after.index)) return;
                toChart.push(after);
                splitToChartRef.current.delete(after.index);
            } else if (isEnabled(before) && !isEnabled(after)) {
                // The hide deletes the chord that followed it (chartFileLink.ts)
                if (chartChords.some(e => linkedTo(e, fileKey, before) && follows(e, before))) {
                    hiddenFromChartRef.current.add(after.index);
                }
            } else if (!isEnabled(before) && isEnabled(after) && hiddenFromChartRef.current.has(after.index)) {
                toChart.push(after);
                hiddenFromChartRef.current.delete(after.index);
            }
        });
        reportedAgainstRef.current = chartChords;
        onRegionsSynced(fileKey, changes, regions, keys, toChart, afterUnlinked, chartKeeps);
        // onRegionsSynced is App's latest whenever this runs
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [regions, playbackState.isScrubbing, fileKey, notationLegible, keyAreas, fileGlobalKey, followOn, reportsHeld]);

    // Settling a chart key that differs from the file's: the analysis takes
    // the chart's, as the user's own, or the chart goes back to the file's
    // key there (App). The chart's key goes where the file's key change
    // landing there starts, else at the marker's region; a key change the
    // chart cleared is cleared in the file too.
    const useChartKeyAt = (marker: KeyMarker) => {
        recordEditFromChart();
        const refs = regionRefs(regions);
        const id = keyAreaId(marker.changeRegionIndex !== null ? marker.changeRegionIndex : marker.regionIndex);
        if (marker.cleared) setKeyAreas(clearKeyCenterAtChord(refs, keyAreas, id));
        else if (marker.keyCenter === null) setKeyAreas(setNoKeyCenterAtChord(refs, keyAreas, id));
        else setKeyAreas(setKeyCenterAtChord(refs, keyAreas, id, marker.keyCenter));
    };
    const keyMarkers = divergentChartKeys
        .map(d => {
            const region = regions.find(r => Math.abs(r.start - d.seconds) < 0.001);
            const change = d.changeStart === null ? undefined : regions.find(r => Math.abs(r.start - d.changeStart!) < 0.001);
            return region ? {
                regionIndex: region.index, start: region.start, chordId: d.chordId, keyCenter: d.keyCenter,
                cleared: d.cleared, changeRegionIndex: change ? change.index : null, addedChord: d.addedChord,
            } : null;
        })
        .filter((m): m is KeyMarker => m !== null);

    // Follow: the latest chart timestamp of this file at or before the file's
    // place, reported when it changes, so the chart selects the chord the file
    // has reached without hearing of every tick. The place is the playhead, or
    // the start of a region clicked (or split off) since it last moved: a
    // click selects a region without moving the playhead. Kept as the
    // region, not its start, so a boundary dragged since moves the place too.
    // A boundary drag scrubs, but the place stays the selected region: the
    // chart's selection holds still until the drag is let go.
    const [clickedRegion, setClickedRegion] = useState<number | null>(null);
    const boundaryDragRef = useRef(false);
    const [boundaryDrag, setBoundaryDrag] = useState(false);
    const handleBoundaryDrag = (dragging: boolean) => {
        boundaryDragRef.current = dragging;
        setBoundaryDrag(dragging);
        if (dragging && selectedRegions.length === 1) setClickedRegion(selectedRegions[0]);
    };
    useEffect(() => {
        if (!boundaryDragRef.current) setClickedRegion(null);
    }, [playbackState.currentTime]);
    const placeRegion = clickedRegion !== null ? regions.find(r => r.index === clickedRegion) : undefined;
    const followPlace = placeRegion ? placeRegion.start : playbackState.currentTime;
    const followTimes = useMemo(() => {
        if (!fileKey) return [];
        // A hidden region's chord is not followed: the chord before it stays lit
        const hidden = regions.filter(r => !isEnabled(r));
        const times = chartChords
            .filter(entry => entry.midiSource && entry.midiSource.fileKey === fileKey
                && !hidden.some(r => linkedTo(entry, fileKey, r)))
            .map(entry => entry.midiSource!.seconds);
        return Array.from(new Set(times)).sort((a, b) => a - b);
    }, [chartChords, fileKey, regions]);
    const lastFollowTimeRef = useRef<string>('');
    useEffect(() => {
        if (boundaryDrag || reportedAgainstRef.current === chartChords) return;
        let reached: number | null = null;
        if (followOn && fileKey) {
            for (let i = 0; i < followTimes.length && followTimes[i] <= followPlace + 0.0005; i++) reached = followTimes[i];
        }
        const report = `${followOn && fileKey ? fileKey : ''}:${reached === null ? '' : reached}`;
        if (report === lastFollowTimeRef.current) return;
        lastFollowTimeRef.current = report;
        onFollowTime(followOn ? fileKey : null, reached);
        // onFollowTime is App's latest whenever this runs
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [followOn, fileKey, followTimes, followPlace, boundaryDrag]);
    const mergeSelectedRegions = () => {
        if (!mergeRange || !midiAnalysis) return;
        recordRegionEdit(regions);
        const next = mergeRegions(regions, mergeRange[0], mergeRange[1], midiAnalysis.sounding);
        setRegions(next);
        // A key area starting on a region merged away starts at the next
        // region of its own, as a chart key does when its first chord goes
        setKeyAreas(reconcileRegions(regionRefs(regions), regionRefs(next), keyAreas));
        selectRegion(regions[mergeRange[0]].index, next);
    };

    // Region edit mode, for one selected region while paused. Entering puts
    // its corrected notes on the keys (and sounds them); from then on every
    // change to the toggled notes is written into the region as corrections:
    // notes added across it, file notes struck out. One undo step for the lot.
    const canEditRegion = selectedRegions.length === 1 && !playbackState.isPlaying;
    const startRegionEdit = (index: number) => {
        if (playbackState.isPlaying || !regions.some(r => r.index === index)) return;
        recordRegionEdit(regions);
        selectRegion(index);
        setEditingRegion(index);
    };
    const toggleRegionEdit = () => {
        if (editingRegion !== null) setEditingRegion(null);
        else if (canEditRegion) startRegionEdit(selectedRegions[0]);
    };
    useEffect(() => {
        if (editingRegion === null) return;
        const region = regions.find(r => r.index === editingRegion);
        if (!region) {
            setEditingRegion(null);
            return;
        }
        const notes = Array.from(new Set(toggledNotes)).sort((a, b) => a - b);
        if (voicingKey(notes) === voicingKey(regionVoicing(region))) return;
        setRegions(prev => prev.map(r => (r.index === editingRegion ? withAmendments(r, amendmentsFor(r, notes)) : r)));
        // regions is read only to find the region being edited
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [toggledNotes, editingRegion]);

    // The marker's other choice: the region takes its chart chord's notes,
    // as corrections
    const updateRegionFromChart = (index: number, entryId: string) => {
        const region = regions.find(r => r.index === index);
        if (!fileKey || !region) return;
        const entry = chartChords.find(e => e.id === entryId && linkedTo(e, fileKey, region));
        if (!entry) return;
        recordEditFromChart();
        setRegions(regions.map(r => (r.index === index ? withAmendments(r, amendmentsFor(r, entry.voicing)) : r)));
    };

    // Visibility: the selected regions all become the opposite of the first,
    // shown as chords or hidden as melody
    const canToggleEnabled = selectedInOrder.length > 0;
    const toggleSelectedEnabled = () => {
        if (!canToggleEnabled) return;
        const enable = !isEnabled(selectedInOrder[0]);
        recordRegionEdit(regions);
        setRegions(regions.map(r => (selectedRegions.includes(r.index) ? { ...r, enabled: enable } : r)));
    };

    // Split: a new pin at the playhead, splitting the region it is in. Not
    // while playing, when it would flash off at every boundary the playhead
    // crosses.
    const MIN_NEW_REGION_SECONDS = 0.05;
    const canSplitRegion = (() => {
        if (playbackState.isPlaying) return false;
        const k = regionAt(regions, playbackState.currentTime);
        if (k < 0) return false;
        return playbackState.currentTime - regions[k].start >= MIN_NEW_REGION_SECONDS
            && regions[k].end - playbackState.currentTime >= MIN_NEW_REGION_SECONDS;
    })();
    const splitRegionAtPlayhead = () => {
        if (!midiAnalysis || !canSplitRegion) return;
        const index = Math.max(...regions.map(r => r.index)) + 1;
        const next = splitRegion(regions, playbackState.currentTime, index, MIN_NEW_REGION_SECONDS, midiAnalysis.sounding);
        if (next === regions) return;
        // The chart keeps in step: a charted region split in two is two chords
        // (asked about first if Follow is off until the report goes)
        const split = regions[regionAt(regions, playbackState.currentTime)];
        if (fileKey && isEnabled(split) && isCharted(chartChords, fileKey, split)) {
            splitToChartRef.current.add(index);
        }
        recordRegionEdit(regions);
        setRegions(next);
        setSelectedRegions([index]);
        setSelectionAnchor(index);
        setClickedRegion(index);
    };

    const undoRegionEdit = () => {
        const { past, future } = regionHistory;
        if (past.length === 0) return;
        setEditingRegion(null);
        const undone = past[past.length - 1];
        setRegionHistory({ past: past.slice(0, -1), future: [{ regions, keyAreas, globalKey: fileGlobalKey, fromChart: undone.fromChart }, ...future] });
        if (undone.fromChart) chartKeepsRef.current = true;
        setRegions(undone.regions);
        setKeyAreas(undone.keyAreas);
        setFileGlobalKey(undone.globalKey);
    };

    const redoRegionEdit = () => {
        const { past, future } = regionHistory;
        if (future.length === 0) return;
        setEditingRegion(null);
        setRegionHistory({ past: [...past, { regions, keyAreas, globalKey: fileGlobalKey, fromChart: future[0].fromChart }], future: future.slice(1) });
        setRegions(future[0].regions);
        setKeyAreas(future[0].keyAreas);
        setFileGlobalKey(future[0].globalKey);
    };

    // Handle file upload
    const handleFileUpload = useCallback(async (event: React.ChangeEvent<HTMLInputElement>) => {
        const file = event.target.files && event.target.files[0];
        if (!file) return;

        try {
            processingSessionRef.current += 1;
            processingCancelRef.current = false;
            setIsReadingMidi(true);
            const mySession = processingSessionRef.current;
            analyzeChords(file, mySession);
            const parsed = await parseMidiFile(file);
            if (processingCancelRef.current || mySession !== processingSessionRef.current) { setIsReadingMidi(false); setProcessingProgress(0); return; }
            setParsedMidi(parsed);

            await preComputeFileData(parsed);
            if (processingCancelRef.current || mySession !== processingSessionRef.current) { setIsReadingMidi(false); setProcessingProgress(0); return; }

            setPlaybackState(prev => ({
                ...prev,
                duration: parsed.duration,
                currentTime: 0,
                isPlaying: false
            }));

            console.log('MIDI file loaded:', parsed.name, `${parsed.duration.toFixed(2)}s`);
        } catch (error) {
            console.error('Error loading MIDI file:', error);
            alert('Error loading MIDI file. Please try a different file.');
        } finally {
            setIsReadingMidi(false);
            // Reset file input value so same file can be selected again
            event.target.value = '';
        }
    }, [preComputeFileData, analyzeChords]);

    // Handle file drop
    const handleDrop = useCallback(async (event: React.DragEvent<HTMLDivElement>) => {
        event.preventDefault();
        event.stopPropagation();
        setIsDragOver(false);

        const files = event.dataTransfer.files;
        if (!files || files.length === 0) return;

        const file = files[0];
        if (!file.name.match(/\.(mid|midi)$/i)) {
            alert('Please drop a MIDI file (.mid or .midi)');
            return;
        }

        try {
            processingSessionRef.current += 1;
            processingCancelRef.current = false;
            setIsReadingMidi(true);
            const mySession = processingSessionRef.current;
            analyzeChords(file, mySession);
            const parsed = await parseMidiFile(file);
            if (processingCancelRef.current || mySession !== processingSessionRef.current) { setIsReadingMidi(false); setProcessingProgress(0); return; }
            setParsedMidi(parsed);

            await preComputeFileData(parsed);
            if (processingCancelRef.current || mySession !== processingSessionRef.current) { setIsReadingMidi(false); setProcessingProgress(0); return; }
        } catch (error) {
            console.error('Error loading MIDI file:', error);
            alert('Error loading MIDI file. Please try a different file.');
        } finally {
            setIsReadingMidi(false);
        }
    }, [preComputeFileData, analyzeChords]);

    const handleDragOver = useCallback((event: React.DragEvent<HTMLDivElement>) => {
        event.preventDefault();
        event.stopPropagation();
        setIsDragOver(true);
    }, []);

    const handleDragLeave = useCallback((event: React.DragEvent<HTMLDivElement>) => {
        event.preventDefault();
        event.stopPropagation();
        setIsDragOver(false);
    }, []);

    // Streaming event playback - inspired by the responsive scrubbing approach
    const streamingPlaybackRef = useRef<number | null>(null);
    const lastPlaybackTimeRef = useRef<number>(0);
    const processedEventsSetRef = useRef<Set<string>>(new Set());

    const scheduleEvents = useCallback(() => {
        if (!parsedMidi || !midiEventMerger) return;

        // Clear existing events
        scheduledEventsRef.current.forEach(id => Tone.getTransport().clear(id));
        scheduledEventsRef.current = [];

        // Stop any existing streaming playback and clear processed events
        if (streamingPlaybackRef.current) {
            clearInterval(streamingPlaybackRef.current);
            streamingPlaybackRef.current = null;
        }
        processedEventsSetRef.current.clear(); // Clear processed events for fresh start

        // Reset lastPlaybackTimeRef to current transport position to prevent event accumulation
        lastPlaybackTimeRef.current = Tone.getTransport().seconds * playbackState.playbackSpeed;

        // Use streaming approach similar to scrubbing - process events as we go

        const processEventsStreaming = () => {
            const currentTime = Tone.getTransport().seconds * playbackState.playbackSpeed;
            const lastTime = lastPlaybackTimeRef.current;
            const lookaheadTime = 0.05; // 50ms lookahead for smooth playback
            const minTimeThreshold = 0.001; // 1ms minimum time difference to prevent duplicate processing
            const inWindow = (t: number) => t > lastTime + minTimeThreshold && t <= currentTime + lookaheadTime;

            // This window's events, sent in time order with pedal events
            // before notes at the same instant, as preComputeFileData bakes
            // the pedal (a key released on the pedal-down tick rings on) and
            // as the backend's `sounding` does. Otherwise playback would
            // release a note that scrubbing, the zoom view's roll and the
            // region names all hear ringing. The sort is stable, so notes at
            // one instant keep their file order, and pedal events theirs.
            const batch: Array<{ time: number; pedal: boolean; eventId: string; data: number[] }> = [];
            parsedMidi.notes.forEach((note, index) => {
                if (inWindow(note.time)) {
                    batch.push({ time: note.time, pedal: false, eventId: `note-on-${index}-${note.time.toFixed(6)}`, data: [144, note.note, note.velocity] });
                }
                const noteOffTime = note.time + note.duration;
                if (inWindow(noteOffTime)) {
                    batch.push({ time: noteOffTime, pedal: false, eventId: `note-off-${index}-${noteOffTime.toFixed(6)}`, data: [128, note.note, note.velocity] });
                }
            });
            parsedMidi.controlChanges
                .filter(cc => cc.controller === 64)
                .forEach((cc, index) => {
                    if (inWindow(cc.time)) {
                        batch.push({ time: cc.time, pedal: true, eventId: `cc-${index}-${cc.time.toFixed(6)}`, data: [176, cc.controller, cc.value] });
                    }
                });
            batch.sort((a, b) => (a.time - b.time) || (a.pedal === b.pedal ? 0 : a.pedal ? -1 : 1));

            batch.forEach(event => {
                if (processedEventsSetRef.current.has(event.eventId)) return;
                processedEventsSetRef.current.add(event.eventId);
                midiEventMerger.handleFileMidi(event.data, false, false, !isPlayingRef.current);
                sendToMidiOutput(event.data);
            });

            lastPlaybackTimeRef.current = currentTime;
        };

        // Start streaming playback - check every 10ms for events to trigger
        streamingPlaybackRef.current = setInterval(processEventsStreaming, 10) as unknown as number;
        lastPlaybackTimeRef.current = Tone.getTransport().seconds * playbackState.playbackSpeed;

    }, [parsedMidi, midiEventMerger, playbackState.playbackSpeed, sendToMidiOutput]);


    // Enhanced helper function to get individual note events (including re-strikes)
    const getActiveNoteEventsAtTime = (time: number) => {
        const activeEvents: Array<{
            noteNumber: number;
            originalStartTime: number;
            velocity: number;
            eventId: string;
        }> = [];

        enhancedNotes.forEach(note => {
            if (time >= note.time && time < note.effectiveEndTime) {
                activeEvents.push({
                    noteNumber: note.note,
                    originalStartTime: note.time,
                    velocity: note.velocity,
                    eventId: `${note.note}-${note.time.toFixed(3)}`
                });
            }
        });
        return activeEvents;
    };

    // Handle scrub position change
    const handleScrubMove = useCallback((newTime: number) => {
        if (isComputingNav) return;
        if (!parsedMidi || !midiEventMerger) return;

        const previousTime = previousScrubTimeRef.current;
        const isMovingForward = newTime > previousTime;

        const currentActiveNotes = scrubbingActiveNotesRef.current;
        const currentActiveEventIds = scrubbingActiveEventIdsRef.current;

        // Enhanced event-based calculation (detects re-strikes)
        const newActiveEvents = getActiveNoteEventsAtTime(newTime);
        const newActiveNotes = new Set<number>();
        const newActiveEventIds = new Set<string>();

        newActiveEvents.forEach(event => {
            newActiveNotes.add(event.noteNumber);
            newActiveEventIds.add(event.eventId);
        });



        // Handle sustain state based on movement direction
        if (isMovingForward) {
            if (playbackState.isScrubbing) {
                // Forward scrubbing while paused: disable file sustain, only respect live sustain
                // This prevents file sustain from interfering with live playing
                midiEventMerger.updateFileSustainState(false);
                const finalSustainState = midiEventMerger.getFinalSustainState();
                onSustainStateChange(finalSustainState);

                // Only send sustain off if live sustain isn't active
                if (midiEventMerger.getLiveSustainState() !== true) {
                    const sustainData = [176, 64, 0];
                    midiEventMerger.handleFileMidi(sustainData, false, false, !isPlayingRef.current);
                    sendToMidiOutput(sustainData);
                }
                scrubbingSustainStateRef.current = finalSustainState;
            } else {
                // Forward navigation buttons: disable file sustain, respect live sustain priority
                midiEventMerger.updateFileSustainState(false);
                const finalSustainState = midiEventMerger.getFinalSustainState();
                onSustainStateChange(finalSustainState);

                // Only send sustain off if live sustain isn't active
                if (midiEventMerger.getLiveSustainState() !== true) {
                    const sustainData = [176, 64, 0];
                    midiEventMerger.handleFileMidi(sustainData, false, false, !isPlayingRef.current);
                    sendToMidiOutput(sustainData);
                }
                scrubbingSustainStateRef.current = finalSustainState;
            }
        } else {
            // Backward movement: disable file sustain, respect live sustain priority
            midiEventMerger.updateFileSustainState(false);
            const finalSustainState = midiEventMerger.getFinalSustainState();
            onSustainStateChange(finalSustainState);

            // Only send sustain off if live sustain isn't active
            if (midiEventMerger.getLiveSustainState() !== true) {
                const sustainData = [176, 64, 0];
                midiEventMerger.handleFileMidi(sustainData, false, false, !isPlayingRef.current);
                sendToMidiOutput(sustainData);
            }
            scrubbingSustainStateRef.current = finalSustainState;
        }

        // Backward movement restrikes the note-on moments we crossed, for notes
        // that are still sounding at the target. Keep only the LAST crossing of
        // each note: the earlier ones would all fire in this same audio instant,
        // where they can't be heard as separate attacks and only stack copies of
        // one sample on top of each other (which is what distorted the output).
        const crossedRestrikes = new Map<number, number[]>();
        if (!isMovingForward) {
            const minTime = Math.min(previousTime, newTime);
            const maxTime = Math.max(previousTime, newTime);

            precomputedEvents.forEach(event => {
                if (event.isNoteOn &&
                    event.time > minTime &&
                    event.time <= maxTime &&
                    Math.abs(event.time - newTime) > 0.001 && // Exclude events at target time (handled below)
                    newActiveNotes.has(event.data[1])) { // Note should currently be active
                    // precomputedEvents is time-sorted, so the last write wins
                    crossedRestrikes.set(event.data[1], event.data);
                }
            });
        }

        // Enhanced event-based note on/off logic using precomputed events
        const eventsToTrigger = newActiveEvents.filter(event => !currentActiveEventIds.has(event.eventId));

        // Everything below lands in a single audio instant, so the restrikes and
        // the new note-ons are trimmed together as one batch
        const velocityTrim = getBatchVelocityTrim(eventsToTrigger.length + crossedRestrikes.size);

        const triggerNoteOns = () => {
            // Turn on new events (including re-strikes of same notes)
            eventsToTrigger.forEach(event => {
                const noteOnData = [144, event.noteNumber, trimVelocity(event.velocity, velocityTrim)];
                midiEventMerger.handleFileMidi(noteOnData, true, true, !isPlayingRef.current); // Precomputed note-on
                sendToMidiOutput(noteOnData);
            });
        };

        const triggerNoteOffs = () => {
            // Turn off events that are no longer active
            currentActiveNotes.forEach(note => {
                if (!newActiveNotes.has(note)) {
                    const noteOffData = [128, note, 0];
                    midiEventMerger.handleFileMidi(noteOffData, true, false, !isPlayingRef.current); // Precomputed note-off
                    sendToMidiOutput(noteOffData);
                }
            });
        };

        if (isMovingForward) {
            triggerNoteOns();
            triggerNoteOffs();
        } else {
            // Backward: turn off old events first, then turn on new ones
            triggerNoteOffs();
            triggerNoteOns();

            crossedRestrikes.forEach(data => {
                const restrikeData = [data[0], data[1], trimVelocity(data[2], velocityTrim)];
                midiEventMerger.handleFileMidi(restrikeData, true, true, !isPlayingRef.current); // isPrecomputed=true, isNoteOn=true
                sendToMidiOutput(restrikeData);
            });
        }

        // Update tracking (both notes and event IDs)
        scrubbingActiveNotesRef.current = newActiveNotes;
        scrubbingActiveEventIdsRef.current = newActiveEventIds;
        previousScrubTimeRef.current = newTime;
        setPlaybackState(prev => ({ ...prev, currentTime: newTime }));

    }, [parsedMidi, midiEventMerger, getSustainStateAtTime, onSustainStateChange, sendToMidiOutput, enhancedNotes, precomputedEvents, isComputingNav]);

    // Where a scrub's mouse position lands in the file. The progress bar and
    // the zoomed view map the pointer differently (a pin also stops at its
    // neighbours), so whichever starts the scrub sets it; the moves and the
    // release are then the same code for both.
    const scrubTimeAtRef = useRef<((clientX: number) => number) | null>(null);

    // Start scrubbing at a point in the file
    const startScrubAt = useCallback((newTime: number) => {
        if (isComputingNav) return;
        if (!parsedMidi) return;

        onFileInteraction();
        setEditingRegion(null);

        if (playbackState.isPlaying) {
            Tone.getTransport().pause();
        }

        // Enhanced event-based calculation for initialization
        const activeEvents = getActiveNoteEventsAtTime(newTime);
        const activeNotes = new Set<number>();
        const activeEventIds = new Set<string>();

        activeEvents.forEach(event => {
            activeNotes.add(event.noteNumber);
            activeEventIds.add(event.eventId);
        });

        const sustainState = getSustainStateAtTime(newTime);

        scrubbingActiveNotesRef.current = activeNotes;
        scrubbingActiveEventIdsRef.current = activeEventIds;
        scrubbingSustainStateRef.current = sustainState;
        previousScrubTimeRef.current = newTime;

        setPlaybackState(prev => ({ ...prev, isScrubbing: true }));

        if (midiEventMerger) {
            midiEventMerger.updateFileSustainState(sustainState);
            const finalSustainState = midiEventMerger.getFinalSustainState();
            onSustainStateChange(finalSustainState);
        }

        clearAllNotes();

        // Trigger initial notes
        if (midiEventMerger) {
            if (sustainState) {
                const sustainData = [176, 64, 127];
                midiEventMerger.handleFileMidi(sustainData, false, false, !isPlayingRef.current); // Not precomputed
                sendToMidiOutput(sustainData);
            }
            // Clicking into a dense moment fires this whole set at once, so trim
            // it as one batch the same way a scrub move does
            const velocityTrim = getBatchVelocityTrim(activeNotes.size);
            activeNotes.forEach(noteNumber => {
                const noteObj = enhancedNotes.find(n => n.note === noteNumber);
                const velocity = noteObj ? noteObj.velocity : 80;
                const noteOnData = [144, noteNumber, trimVelocity(velocity, velocityTrim)];
                midiEventMerger.handleFileMidi(noteOnData, true, true, !isPlayingRef.current); // Precomputed note-on
                sendToMidiOutput(noteOnData);
            });
        }

        setPlaybackState(prev => ({
            ...prev,
            isScrubbing: true,
            currentTime: newTime
        }));
        onScrubbingStateChange(true);
    }, [parsedMidi, playbackState.isPlaying, clearAllNotes, midiEventMerger, enhancedNotes, getSustainStateAtTime, onSustainStateChange, sendToMidiOutput, isComputingNav, onFileInteraction]);

    // Handle scrub start on the progress bar
    const handleScrubStart = useCallback((event: React.MouseEvent) => {
        if (!parsedMidi || !progressBarRef.current) return;
        const timeAt = (clientX: number) => {
            if (!progressBarRef.current) return previousScrubTimeRef.current;
            const rect = progressBarRef.current.getBoundingClientRect();
            const percentage = Math.max(0, Math.min(1, (clientX - rect.left) / rect.width));
            return percentage * parsedMidi.duration;
        };
        scrubTimeAtRef.current = timeAt;
        startScrubAt(timeAt(event.clientX));
    }, [parsedMidi, startScrubAt]);

    // Scrub started in the zoomed view: on the roll, or by grabbing a pin
    const handleViewScrubStart = useCallback((time: number, timeAt: (clientX: number) => number) => {
        scrubTimeAtRef.current = timeAt;
        startScrubAt(time);
    }, [startScrubAt]);

    // Starting playback part-way into a file only streams events AFTER that
    // point, so everything already sounding there - a chord struck before the
    // playhead, notes the pedal is still carrying - was simply missing, and the
    // harmony at the resume point could not be seen or heard against what comes
    // next. Rebuild that moment from the precomputed data by replaying the
    // smallest set of events that produces it: pedal down, then note-ons, then
    // note-offs for the notes whose key is already up. Going through the normal
    // file-MIDI path (rather than setting the note lists directly) is what puts
    // each note in the right list - keys still down land in activeNotes, the
    // rest are moved to heldNotes by their own note-off - so the note-offs and
    // the pedal-up event still ahead of the playhead release them correctly.
    // What the file is doing at a point in time, from the precomputed data: look
    // back for every note that started at or before it and is still sounding
    // there (effectiveEndTime has the pedal baked in), then split by whether the
    // key itself is still down. The same pitch can have more than one strike
    // still sounding - struck twice under one pedal - but only the most recent
    // strike describes the state of that key now, so it decides the pitch's list.
    const getFileStateAtTime = useCallback((time: number) => {
        const sustainState = getSustainStateAtTime(time);

        const soundingByPitch = new Map<number, any>();
        enhancedNotes.forEach(note => {
            if (note.time > time) return;                  // hasn't started yet
            if (note.effectiveEndTime <= time) return;     // finished, pedal included
            const latest = soundingByPitch.get(note.note);
            if (!latest || note.time > latest.time) {
                soundingByPitch.set(note.note, note);
            }
        });

        const keysStillDown: Array<any> = [];
        const pedalHolding: Array<any> = [];

        soundingByPitch.forEach(note => {
            if (note.time + note.duration > time) {
                keysStillDown.push(note);                  // key still down
            } else if (sustainState) {
                pedalHolding.push(note);                   // key up, pedal carrying it
            }
        });

        return { sustainState, keysStillDown, pedalHolding };
    }, [enhancedNotes, getSustainStateAtTime]);

    // Notes that are sounding right now AND should still be sounding at `time`.
    // Their samples are left alone across the resume: not released, not struck
    // again, only moved into the right list.
    const getCarryOverNotes = useCallback((time: number): number[] => {
        const soundingNow = new Set<number>([...activeNotes, ...heldNotes, ...toggledNotes]);
        if (soundingNow.size === 0) return [];

        const { keysStillDown, pedalHolding } = getFileStateAtTime(time);
        return keysStillDown.concat(pedalHolding)
            .map(note => note.note)
            .filter(pitch => soundingNow.has(pitch));
    }, [activeNotes, heldNotes, toggledNotes, getFileStateAtTime]);

    const seedNotesAtTime = useCallback((time: number, alreadySounding: number[] = []) => {
        if (!midiEventMerger) return;

        const { sustainState, keysStillDown, pedalHolding } = getFileStateAtTime(time);

        midiEventMerger.updateFileSustainState(sustainState);
        onSustainStateChange(midiEventMerger.getFinalSustainState());

        if (sustainState) {
            const sustainData = [176, 64, 127];
            midiEventMerger.handleFileMidi(sustainData, false, false, false);
            sendToMidiOutput(sustainData);
        }

        if (keysStillDown.length === 0 && pedalHolding.length === 0) return;

        // This whole moment sounds at once, so trim it as one batch
        const velocityTrim = getBatchVelocityTrim(keysStillDown.length + pedalHolding.length);
        const carriedOver = new Set(alreadySounding);

        keysStillDown.concat(pedalHolding).forEach(note => {
            // A carried-over note is already ringing here and on the output
            // device: restore it into the right list with no onset at all, so it
            // is neither struck again nor flashed on a key that never went dark
            const isCarriedOver = carriedOver.has(note.note);
            const noteOnData = [144, note.note, trimVelocity(note.velocity, velocityTrim)];
            midiEventMerger.handleFileMidi(noteOnData, false, false, false, isCarriedOver);
            if (!isCarriedOver) {
                sendToMidiOutput(noteOnData);
            }
        });

        // With the pedal down this note-off does not release the sample; it hands
        // the note to the pedal, so the pedal-up event ahead of us releases it
        pedalHolding.forEach(note => {
            const noteOffData = [128, note.note, 0];
            midiEventMerger.handleFileMidi(noteOffData, false, false, false);
            sendToMidiOutput(noteOffData);
        });
    }, [midiEventMerger, getFileStateAtTime, onSustainStateChange, sendToMidiOutput]);

    // Handle scrub end
    const handleScrubEnd = useCallback(() => {
        const finalScrubTime = previousScrubTimeRef.current;

        if (midiEventMerger) {
            midiEventMerger.clearFileState();
            midiEventMerger.updateFileSustainState(false);
        }

        // Notes ringing here that belong to this moment keep their samples
        const carryOverNotes = playbackState.isPlaying ? getCarryOverNotes(finalScrubTime) : [];

        if (playbackState.isPlaying) {
            clearAllNotes(carryOverNotes); // Clear for clean playback resume
            // Sustain and the notes sounding here are restored by seedNotesAtTime
            // below, once scrubbing state is off
        } else {
            // When paused/stopped, always keep sustain OFF
            onSustainStateChange(false);
        }

        // Update the playback state with the final scrub position FIRST
        setPlaybackState(prev => ({
            ...prev,
            isScrubbing: false,
            currentTime: finalScrubTime
        }));
        onScrubbingStateChange(false);

        // If we were playing before scrubbing, resume playback
        if (playbackState.isPlaying) {
            // Set transport position to current scrub position adjusted for speed
            Tone.getTransport().position = finalScrubTime / playbackState.playbackSpeed;

            // Restore sustain and whatever is sounding at this point, so playback
            // resumes into the harmony that is actually here rather than silence
            seedNotesAtTime(finalScrubTime, carryOverNotes);

            // Reschedule events from current position
            scheduleEvents();
            Tone.getTransport().start();
        }

    }, [playbackState.isPlaying, playbackState.playbackSpeed, scheduleEvents, seedNotesAtTime, getCarryOverNotes, clearAllNotes, midiEventMerger, onSustainStateChange, onScrubbingStateChange]);

    // mousemove fires far faster than the screen refreshes (120+/sec on a modern
    // trackpad), and every event ran a full scrub update: a scan of every note in
    // the file, plus a React flash update and two timers per note triggered. That
    // both floods the audio with bursts a few milliseconds apart and starves the
    // main thread. Keep only the latest position and apply it once per frame -
    // the display can't show more than that anyway, so nothing is lost.
    const pendingScrubTimeRef = useRef<number | null>(null);
    const scrubFrameRef = useRef<number | null>(null);

    const flushPendingScrub = useCallback(() => {
        if (scrubFrameRef.current !== null) {
            cancelAnimationFrame(scrubFrameRef.current);
            scrubFrameRef.current = null;
        }
        const pendingTime = pendingScrubTimeRef.current;
        pendingScrubTimeRef.current = null;
        if (pendingTime !== null) {
            handleScrubMove(pendingTime);
        }
    }, [handleScrubMove]);

    // Handle mouse up during scrubbing
    const handleScrubMouseUp = useCallback(() => {
        if (playbackState.isScrubbing) {
            // Apply any position still waiting on a frame, so releasing the mouse
            // lands where the mouse actually was
            flushPendingScrub();
            handleScrubEnd();
        }
    }, [playbackState.isScrubbing, flushPendingScrub, handleScrubEnd]);

    // Handle mouse move during scrubbing
    const handleScrubMouseMove = useCallback((event: MouseEvent) => {
        if (!playbackState.isScrubbing || !parsedMidi || !scrubTimeAtRef.current) return;

        // Releasing the button outside the browser window never delivers a
        // mouseup, which left the scrub (and its cursor) stuck on. A move that
        // arrives with no button held means the release happened where we could
        // not see it, so end the scrub at the last position we did see.
        if (event.buttons === 0) {
            handleScrubMouseUp();
            return;
        }

        pendingScrubTimeRef.current = scrubTimeAtRef.current(event.clientX);
        if (scrubFrameRef.current === null) {
            scrubFrameRef.current = requestAnimationFrame(() => {
                scrubFrameRef.current = null;
                const frameTime = pendingScrubTimeRef.current;
                pendingScrubTimeRef.current = null;
                if (frameTime !== null) {
                    handleScrubMove(frameTime);
                }
            });
        }
    }, [playbackState.isScrubbing, parsedMidi, handleScrubMove, handleScrubMouseUp]);

    // Add global mouse event listeners for scrubbing
    useEffect(() => {
        if (playbackState.isScrubbing) {
            document.addEventListener('mousemove', handleScrubMouseMove);
            document.addEventListener('mouseup', handleScrubMouseUp);
            // Covers a release outside the window that is never followed by a
            // move back over the page (dragging out and clicking another app)
            window.addEventListener('blur', handleScrubMouseUp);
        }

        return () => {
            document.removeEventListener('mousemove', handleScrubMouseMove);
            document.removeEventListener('mouseup', handleScrubMouseUp);
            window.removeEventListener('blur', handleScrubMouseUp);
            if (scrubFrameRef.current !== null) {
                cancelAnimationFrame(scrubFrameRef.current);
                scrubFrameRef.current = null;
            }
            pendingScrubTimeRef.current = null;
        };
    }, [playbackState.isScrubbing, handleScrubMouseMove, handleScrubMouseUp]);

    // Hold the scrub cursor for the whole drag. The cursor on the progress bar
    // only applies while the pointer is over it, but the drag continues wherever
    // the mouse goes, so the class stays on <body> until the mouse is released.
    // Kept in its own effect so it isn't torn down and re-added every time the
    // mouse handlers above are rebuilt.
    useEffect(() => {
        if (!playbackState.isScrubbing) return;

        document.body.classList.add('scrubbing-midi');
        return () => {
            document.body.classList.remove('scrubbing-midi');
        };
    }, [playbackState.isScrubbing]);


    // Group events within 30ms time window, skip silent moments and duplicate note states
    const getGroupedNoteEvents = useCallback(() => {
        if (!enhancedNotes.length) return [];

        // Get all unique event times
        const eventTimes = new Set<number>();
        enhancedNotes.forEach(note => {
            eventTimes.add(note.time);
            eventTimes.add(note.effectiveEndTime);
        });

        const sortedTimes = Array.from(eventTimes).sort((a, b) => a - b);
        const groupedEvents: number[] = [];
        const timeWindow = 0.03; // 30ms
        let previousActiveNotes: Set<number> | null = null;

        // Helper function to get active notes at a specific time
        const getActiveNotesAtTime = (time: number): Set<number> => {
            const activeNotes = new Set<number>();
            enhancedNotes.forEach(note => {
                if (time >= note.time && time < note.effectiveEndTime) {
                    activeNotes.add(note.note);
                }
            });
            return activeNotes;
        };

        // Helper function to compare two sets
        const setsAreEqual = (set1: Set<number>, set2: Set<number>): boolean => {
            if (set1.size !== set2.size) return false;
            for (const item of set1) {
                if (!set2.has(item)) return false;
            }
            return true;
        };

        let i = 0;
        while (i < sortedTimes.length) {
            const groupStart = sortedTimes[i];
            let groupEnd = i;

            // Find all events within 30ms window
            while (groupEnd + 1 < sortedTimes.length &&
                sortedTimes[groupEnd + 1] - sortedTimes[i] <= timeWindow) {
                groupEnd++;
            }

            // Check active notes at this time
            const representativeTime = sortedTimes[i];
            const currentActiveNotes = getActiveNotesAtTime(representativeTime);

            // Skip if silent (no notes active)
            const isSilent = currentActiveNotes.size === 0;

            // Skip if identical to previous state
            const isDuplicateState = previousActiveNotes !== null &&
                setsAreEqual(currentActiveNotes, previousActiveNotes);

            // Only add times where notes are playing AND the state has changed
            if (!isSilent && !isDuplicateState) {
                groupedEvents.push(representativeTime);
                previousActiveNotes = new Set(currentActiveNotes); // Store copy for next comparison
            }

            // Move to next ungrouped event
            i = groupEnd + 1;
        }

        return groupedEvents;
    }, [enhancedNotes]);

    // Chord-aware navigation events (for buttons only - scrubbing uses raw times)
    const getGroupedNoteEventsWithRestrikes = useCallback(async (): Promise<number[]> => {
        if (!enhancedNotes.length) return [];

        const scale = (start: number, end: number, frac: number) => {
            const clamped = Math.max(0, Math.min(1, frac));
            setProcessingProgress(Math.round(start + (end - start) * clamped));
        };

        // Build a fast lookup for precomputed note-on times (ms key)
        const precomputedNoteOnTimes = new Set<number>();
        for (let e of precomputedEvents) {
            if (e.isNoteOn) {
                precomputedNoteOnTimes.add(Math.round(e.time * 1000));
            }
        }

        // === DEBUG CONFIGURATION ===
        const debugTimeStart = 14.98; // Range to debug for repeated notes
        const debugTimeEnd = 15.68;

        // === DYNAMIC CHORD DETECTION PARAMETERS ===
        const calculateDynamicGroupingWindow = () => {
            if (enhancedNotes.length < 2) return 0.10;
            const noteStartTimes = enhancedNotes.map(note => note.time).sort((a, b) => a - b);
            const timeDiffs: number[] = [];
            for (let i = 1; i < noteStartTimes.length; i++) {
                const diff = noteStartTimes[i] - noteStartTimes[i - 1];
                if (diff > 0) timeDiffs.push(diff);
            }
            if (timeDiffs.length === 0) return 0.10;
            const avg = timeDiffs.reduce((s, d) => s + d, 0) / timeDiffs.length;
            return Math.min(Math.max(avg * 0.3, 0.02), 0.5);
        };

        const chordOnsetGroupingWindow = calculateDynamicGroupingWindow();
        const chordReleaseGroupingWindow = 0.050; //
        const minimumChordDuration = 0.020; // Reduced from 0.090 to 0.005 (5ms) to avoid grouping fast melodic notes
        const previousChordReleaseWindow = 0.020;
        const maxArpeggioSpread = 0.000;
        const melodicGapIgnoreThreshold = 0.020; // 20ms - ignore micro-gaps between melodic notes when background notes are sustained
        const sustainedNoteDurationThreshold = 0.200; // 500ms - notes longer than this are considered sustained background
        const mySession = processingSessionRef.current;

        // Phase 1: Build chord groups (10% -> 20%)
        const noteStartTimes = enhancedNotes.map(note => note.time).sort((a, b) => a - b);
        const chordGroups: { startTime: number, endTime: number, latestOnset: number }[] = [];
        const totalStartTimes = noteStartTimes.length;
        let i = 0;
        while (i < noteStartTimes.length) {
            if (processingCancelRef.current || mySession !== processingSessionRef.current) return [];
            const groupStart = noteStartTimes[i];
            let groupEnd = i;
            while (groupEnd + 1 < noteStartTimes.length) {
                if (processingCancelRef.current || mySession !== processingSessionRef.current) return [];
                const timeDiff = noteStartTimes[groupEnd + 1] - noteStartTimes[i];
                if (timeDiff <= chordOnsetGroupingWindow || timeDiff <= maxArpeggioSpread) {
                    groupEnd++;
                } else {
                    break;
                }
            }
            const latestOnset = noteStartTimes[groupEnd];
            chordGroups.push({ startTime: groupStart, endTime: latestOnset, latestOnset });
            i = groupEnd + 1;
            if (chordGroups.length % 100 === 0 || chordGroups.length === 1) {
                scale(10, 20, chordGroups.length / Math.max(1, totalStartTimes));
                await new Promise(r => setTimeout(r, 0));
                if (processingCancelRef.current || mySession !== processingSessionRef.current) return [];
            }
        }

        // Phase 2: Map note-ons (20% -> 25%)
        const noteOnAtGroupedTime = new Set<number>();
        for (let k = 0; k < chordGroups.length; k++) {
            if (processingCancelRef.current || mySession !== processingSessionRef.current) return [];
            const group = chordGroups[k];
            const eventsInGroup = precomputedEvents.filter(event => event.isNoteOn && event.time >= group.startTime && event.time <= group.endTime);
            if (eventsInGroup.length > 0) noteOnAtGroupedTime.add(group.latestOnset);
            if (k % 200 === 0) {
                scale(20, 25, k / Math.max(1, chordGroups.length));
                await new Promise(r => setTimeout(r, 0));
                if (processingCancelRef.current || mySession !== processingSessionRef.current) return [];
            }
        }

        // Phase 3: Grouped onsets analysis (25% -> 95%)
        const candidateEvents: number[] = [];
        for (let index = 0; index < chordGroups.length; index++) {
            if (processingCancelRef.current || mySession !== processingSessionRef.current) return [];
            const chordGroup = chordGroups[index];
            const notesAtLatestOnset = getActiveNoteEventsAtTime(chordGroup.latestOnset);
            if (notesAtLatestOnset.length > 0) {
                let chordEndTime = Infinity;
                notesAtLatestOnset.forEach(noteEvent => {
                    const noteObj = enhancedNotes.find(n => n.note === noteEvent.noteNumber && Math.abs(n.time - noteEvent.originalStartTime) < 0.001);
                    if (noteObj) chordEndTime = Math.min(chordEndTime, noteObj.effectiveEndTime);
                });
                if (chordEndTime !== Infinity) {
                    const chordDuration = chordEndTime - chordGroup.latestOnset;
                    if (chordDuration >= minimumChordDuration) {
                        let optimalTime = chordGroup.latestOnset;
                        if (index > 0) {
                            const prevChordGroup = chordGroups[index - 1];
                            let prevChordFullyReleasedBy = 0;
                            enhancedNotes.forEach(note => {
                                if (note.time >= prevChordGroup.startTime && note.time <= prevChordGroup.endTime) {
                                    prevChordFullyReleasedBy = Math.max(prevChordFullyReleasedBy, note.effectiveEndTime);
                                }
                            });
                            optimalTime = Math.max(chordGroup.latestOnset, prevChordFullyReleasedBy + previousChordReleaseWindow);
                        }
                        candidateEvents.push(optimalTime);
                    }
                }
            }
            if (index % 50 === 0) {
                scale(25, 95, index / Math.max(1, chordGroups.length));
                await new Promise(r => setTimeout(r, 0));
                if (processingCancelRef.current || mySession !== processingSessionRef.current) return [];
            }
        }

        // Phase 4: Additions to ringing chords (95% -> 97%)
        for (let index = 0; index < chordGroups.length; index++) {
            if (processingCancelRef.current || mySession !== processingSessionRef.current) return [];
            const chordGroup = chordGroups[index];
            if (index > 0) {
                const alreadyRingingNotes = getActiveNoteEventsAtTime(chordGroup.latestOnset - 0.001);
                const notesAfterAddition = getActiveNoteEventsAtTime(chordGroup.latestOnset + 0.001);
                if (alreadyRingingNotes.length > 0 && notesAfterAddition.length > alreadyRingingNotes.length) {
                    let combinedChordEndTime = Infinity;
                    notesAfterAddition.forEach(noteEvent => {
                        const noteObj = enhancedNotes.find(n => n.note === noteEvent.noteNumber && Math.abs(n.time - noteEvent.originalStartTime) < 0.001);
                        if (noteObj) combinedChordEndTime = Math.min(combinedChordEndTime, noteObj.effectiveEndTime);
                    });
                    if (combinedChordEndTime !== Infinity) {
                        const combinedChordDuration = combinedChordEndTime - chordGroup.latestOnset;
                        if (combinedChordDuration >= minimumChordDuration) candidateEvents.push(chordGroup.latestOnset);
                    }
                }
            }
            if (index % 50 === 0) {
                scale(95, 97, index / Math.max(1, chordGroups.length));
                await new Promise(r => setTimeout(r, 0));
                if (processingCancelRef.current || mySession !== processingSessionRef.current) return [];
            }
        }

        // Phase 5: Dedupe and cleaning (97% -> 98%)
        const noteEndTimes = enhancedNotes.map(note => note.effectiveEndTime);
        const uniqueEndTimes = [...new Set(noteEndTimes)];
        candidateEvents.push(...uniqueEndTimes);
        const sortedCandidates = [...new Set(candidateEvents)].sort((a, b) => a - b);
        await new Promise(r => setTimeout(r, 0));

        // MELODIC GAP FILTERING: Remove micro-gaps between melodic notes when background notes are sustained
        const melodicFilteredCandidates: number[] = [];

        for (let i = 0; i < sortedCandidates.length; i++) {
            const currentTime = sortedCandidates[i];
            const nextTime = i + 1 < sortedCandidates.length ? sortedCandidates[i + 1] : null;

            if (nextTime && (nextTime - currentTime) < melodicGapIgnoreThreshold) {
                // Check if there are sustained background notes during this gap
                const currentNotes = new Set<number>();
                const nextNotes = new Set<number>();

                getActiveNoteEventsAtTime(currentTime).forEach(event => currentNotes.add(event.noteNumber));
                getActiveNoteEventsAtTime(nextTime).forEach(event => nextNotes.add(event.noteNumber));

                // Find notes that are sustained (present in both current and next)
                const sustainedNotes = [...currentNotes].filter(note => nextNotes.has(note));

                // Check if any sustained notes are long-duration background notes
                const hasSustainedBackground = sustainedNotes.some(noteNumber => {
                    const noteEvent = getActiveNoteEventsAtTime(currentTime).find(event => event.noteNumber === noteNumber);
                    if (noteEvent) {
                        const noteObj = enhancedNotes.find(n =>
                            n.note === noteNumber &&
                            Math.abs(n.time - noteEvent.originalStartTime) < 0.001
                        );
                        return noteObj && (noteObj.effectiveEndTime - noteObj.time) > sustainedNoteDurationThreshold;
                    }
                    return false;
                });

                // Skip this event if it's a micro-gap with sustained background notes
                if (hasSustainedBackground && sustainedNotes.length > 0) {
                    if (currentTime >= debugTimeStart && currentTime <= debugTimeEnd) {
                        console.log(`Skipping micro-gap at ${currentTime.toFixed(6)} (gap: ${((nextTime - currentTime) * 1000).toFixed(1)}ms, sustained: [${sustainedNotes.join(',')}])`);
                    }
                    continue; // Skip this candidate event
                }
            }

            // Keep this candidate
            melodicFilteredCandidates.push(currentTime);
        }

        // Debug: Show melodic filtering results
        const candidatesInRange = sortedCandidates.filter(t => t >= debugTimeStart && t <= debugTimeEnd);
        const filteredInRange = melodicFilteredCandidates.filter(t => t >= debugTimeStart && t <= debugTimeEnd);
        console.log(`DEBUG: Melodic gap filtering - ${candidatesInRange.length} → ${filteredInRange.length} candidates in ${debugTimeStart}-${debugTimeEnd} range`);

        const finalEvents: number[] = [];
        let previousActiveNotes: Set<number> | null = null;

        // Toggle to disable duplicate filtering for debugging - set to true to bypass filtering
        const bypassDuplicateFiltering = false;

        for (let idx = 0; idx < melodicFilteredCandidates.length; idx++) {
            if (processingCancelRef.current || mySession !== processingSessionRef.current) return [];
            const time = melodicFilteredCandidates[idx];
            const currentActiveNotes = new Set<number>();
            const activeEvents = getActiveNoteEventsAtTime(time);
            activeEvents.forEach(event => currentActiveNotes.add(event.noteNumber));

            // Check if there are note on events at this time (using our mapping for grouped times)
            const hasNoteOnEvents = noteOnAtGroupedTime.has(time) ||
                precomputedEvents.some(event =>
                    Math.abs(event.time - time) < 0.001 && event.isNoteOn
                );

            if (bypassDuplicateFiltering) {
                // Skip duplicate filtering entirely - include all events
                finalEvents.push(time);
                previousActiveNotes = new Set(currentActiveNotes);
            } else {
                // Fixed duplicate filtering logic - check both directions for true set equality
                const notesAreIdentical = previousActiveNotes !== null &&
                    currentActiveNotes.size === previousActiveNotes.size &&
                    [...currentActiveNotes].every(note => previousActiveNotes!.has(note)) &&
                    [...previousActiveNotes].every(note => currentActiveNotes.has(note));

                // Debug fast melodic sections
                if (time >= debugTimeStart && time <= debugTimeEnd && previousActiveNotes !== null) {
                    console.log(`Time ${time.toFixed(6)}: prev=[${[...previousActiveNotes].join(',')}], curr=[${[...currentActiveNotes].join(',')}], identical=${notesAreIdentical}, hasNoteOns=${hasNoteOnEvents}, willInclude=${!notesAreIdentical || hasNoteOnEvents}`);
                }

                if (!notesAreIdentical || hasNoteOnEvents) {
                    finalEvents.push(time);
                    previousActiveNotes = new Set(currentActiveNotes);
                }
            }

            if (idx % 50 === 0) {
                scale(97, 98, idx / Math.max(1, melodicFilteredCandidates.length));
                await new Promise(r => setTimeout(r, 0));
                if (processingCancelRef.current || mySession !== processingSessionRef.current) return [];
            }
        }

        // Debug: Check what survived duplicate removal
        const testRangeAfterDuplicateRemoval = finalEvents.filter(t => t >= debugTimeStart && t <= debugTimeEnd);
        console.log(`Stage 3 - After duplicate removal in ${debugTimeStart}-${debugTimeEnd} range: ${testRangeAfterDuplicateRemoval.length} events: [${testRangeAfterDuplicateRemoval.map(t => t.toFixed(6)).join(', ')}]`);

        // 6. POST-PROCESSING: Remove silence events and group only complete release sequences

        // Toggle to disable release grouping for debugging - set to true to bypass grouping
        const bypassReleaseGrouping = true;

        const cleanedEvents: number[] = [];
        let eventIndex = 0;

        if (bypassReleaseGrouping) {
            // Skip release grouping entirely - just copy events and remove silence
            while (eventIndex < finalEvents.length) {
                const currentTime = finalEvents[eventIndex];
                const currentNotes = new Set<number>();
                getActiveNoteEventsAtTime(currentTime).forEach(event => currentNotes.add(event.noteNumber));

                // Skip silence events, EXCEPT for the final ending silence
                if (currentNotes.size === 0) {
                    const allEndTimes = enhancedNotes.map(note => note.effectiveEndTime);
                    const absoluteEndTime = allEndTimes.length > 0 ? Math.max(...allEndTimes) : 0;

                    // Allow the final ending silence, but remove all other silent events
                    if (Math.abs(currentTime - absoluteEndTime) < 0.001) {
                        cleanedEvents.push(currentTime);
                    }
                } else {
                    // Keep all non-silence events
                    cleanedEvents.push(currentTime);
                }
                eventIndex++;
            }
        } else {
            // Original release grouping logic
            while (eventIndex < finalEvents.length) {
                const currentTime = finalEvents[eventIndex];
                const currentNotes = new Set<number>();
                getActiveNoteEventsAtTime(currentTime).forEach(event => currentNotes.add(event.noteNumber));

                // Skip silence events, EXCEPT for the final ending silence
                if (currentNotes.size === 0) {
                    const allEndTimes = enhancedNotes.map(note => note.effectiveEndTime);
                    const absoluteEndTime = allEndTimes.length > 0 ? Math.max(...allEndTimes) : 0;

                    // Allow the final ending silence, but remove all other silent events
                    if (Math.abs(currentTime - absoluteEndTime) < 0.001) {
                        cleanedEvents.push(currentTime);
                    }
                    eventIndex++;
                    continue;
                }

                // Look for staggered chord release sequences
                let groupEnd = eventIndex;
                let largestChordSize = currentNotes.size;
                let largestChordTime = currentTime;
                let foundStaggeredRelease = false;

                // Scan ahead within the release window to find staggered releases
                while (groupEnd + 1 < finalEvents.length) {
                    const nextTime = finalEvents[groupEnd + 1];

                    // Only consider events within the release grouping window
                    if (nextTime - currentTime > chordReleaseGroupingWindow) {
                        break;
                    }

                    const nextNotes = new Set<number>();
                    getActiveNoteEventsAtTime(nextTime).forEach(event => nextNotes.add(event.noteNumber));

                    // Check if this is a staggered release:
                    // 1. Fewer notes than the largest chord seen so far (release occurred)
                    // 2. All remaining notes are subset of the largest chord (no new notes added)
                    if (nextNotes.size < largestChordSize &&
                        [...nextNotes].every(note => currentNotes.has(note))) {
                        foundStaggeredRelease = true;
                        groupEnd++;

                        // Track the largest chord in this sequence
                        if (nextNotes.size > largestChordSize) {
                            largestChordSize = nextNotes.size;
                            largestChordTime = nextTime;
                        }
                    } else if (nextNotes.size > largestChordSize) {
                        // Found a larger chord - update our reference but this might not be a release
                        largestChordSize = nextNotes.size;
                        largestChordTime = nextTime;
                        groupEnd++;
                    } else {
                        // New notes added or sequence broken - stop grouping
                        break;
                    }
                }

                if (foundStaggeredRelease && groupEnd > eventIndex) {
                    // Keep the event with the largest collection of notes
                    cleanedEvents.push(largestChordTime);
                    console.log(`Grouped staggered releases: kept largest chord (${largestChordSize} notes) at ${largestChordTime.toFixed(6)}, grouped ${groupEnd - eventIndex + 1} events`);

                    eventIndex = groupEnd + 1;
                } else {
                    // Not a staggered release sequence, keep this event
                    cleanedEvents.push(currentTime);
                    eventIndex++;
                }
            } // End of original release grouping logic
        }

        // Debug: Check what survived release grouping
        const testRangeAfterReleaseGrouping = cleanedEvents.filter(t => t >= debugTimeStart && t <= debugTimeEnd);
        console.log(`Stage 4 - After release grouping in ${debugTimeStart}-${debugTimeEnd} range: ${testRangeAfterReleaseGrouping.length} events: [${testRangeAfterReleaseGrouping.map(t => t.toFixed(6)).join(', ')}]`);

        // Debug: Show what happened in our test range
        const testTimes = [218.12, 218.53, 219.11];
        testTimes.forEach(targetTime => {
            const beforeCleaning = finalEvents.find(t => Math.abs(t - targetTime) < 0.1);
            const afterCleaning = cleanedEvents.find(t => Math.abs(t - targetTime) < 0.1);
            if (beforeCleaning) {
                const hasNoteOns = noteOnAtGroupedTime.has(beforeCleaning) ||
                    precomputedEvents.some(event =>
                        Math.abs(event.time - beforeCleaning) < 0.001 && event.isNoteOn
                    );

                const activeNotes = getActiveNoteEventsAtTime(beforeCleaning);
                const noteNumbers = activeNotes.map(n => n.noteNumber);

                // Check what type of event this is
                const isChordOnset = chordGroups.some(group =>
                    Math.abs(group.latestOnset - beforeCleaning) < 0.001
                );
                const isReleaseEvent = !isChordOnset;

                console.log(`Time ~${targetTime}: before=${beforeCleaning ? beforeCleaning.toFixed(6) : 'none'}, after=${afterCleaning ? afterCleaning.toFixed(6) : 'none'}, hasNoteOns=${hasNoteOns}, activeNotes=[${noteNumbers.join(',')}], isChordOnset=${isChordOnset}, isRelease=${isReleaseEvent}`);
            }
        });

        return cleanedEvents;
    }, [enhancedNotes, precomputedEvents]);

    // Navigate to next/previous note event (using sustain-extended times)
    // Cache grouped events to avoid recomputation on every navigation
    const [groupedEventTimes, setGroupedEventTimes] = useState<number[]>([]);

    // Process grouped events asynchronously to avoid blocking UI
    useEffect(() => {
        if (!enhancedNotes.length) {
            setGroupedEventTimes([]);
            return;
        }

        // If a cancel is in effect, do not start compute
        if (processingCancelRef.current) {
            setIsComputingNav(false);
            return;
        }

        setIsComputingNav(true);
        setProcessingProgress(10);

        const mySession = processingSessionRef.current;
        let cancelled = false;

        const timer = setTimeout(() => {
            (async () => {
                try {
                    if (cancelled || processingCancelRef.current || mySession !== processingSessionRef.current) return;
                    const events = await getGroupedNoteEventsWithRestrikes();
                    if (cancelled || processingCancelRef.current || mySession !== processingSessionRef.current) return;
                    setGroupedEventTimes(events);
                    if (cancelled || processingCancelRef.current || mySession !== processingSessionRef.current) return;
                    setProcessingProgress(100);
                    setTimeout(() => setProcessingProgress(0), 500);
                } catch (error) {
                    console.error('Error processing MIDI events:', error);
                    if (!cancelled) {
                        setGroupedEventTimes([]);
                        setProcessingProgress(0);
                    }
                } finally {
                    if (!cancelled) setIsComputingNav(false);
                }
            })();
        }, 10);

        return () => {
            cancelled = true;
            clearTimeout(timer);
            setIsComputingNav(false);
            setProcessingProgress(0);
        };
    }, [getGroupedNoteEventsWithRestrikes, enhancedNotes.length]);


    const navigateToEvent = useCallback((direction: 'next' | 'prev') => {
        if (!parsedMidi || playbackState.isPlaying || playbackState.isScrubbing || isReadingMidi || isComputingNav) return;

        onFileInteraction();

        // Use cached grouped events for navigation
        const eventTimes = groupedEventTimes;
        const currentTime = playbackState.currentTime;

        let targetTime: number;


        if (direction === 'next') {
            const nextTime = eventTimes.find(time => time > currentTime + 0.0001);
            if (nextTime !== undefined) {
                targetTime = nextTime;
            } else {
                // No next event found - use the last event in our list (should be final silence)
                targetTime = eventTimes.length > 0 ? eventTimes[eventTimes.length - 1] : 0;
            }
        } else {
            const prevTimes = eventTimes.filter(time => time < currentTime - 0.0001);
            if (prevTimes.length > 0) {
                targetTime = prevTimes[prevTimes.length - 1];
            } else {
                // No previous event found - navigate to beginning silence (time 0)
                targetTime = 0;
            }
        }


        if (Math.abs(targetTime - currentTime) > 0.0001) {
            handleScrubMove(targetTime);

            // // Handle sustain state based on navigation direction
            // if (midiEventMerger) {
            //     if (direction === 'next') {
            //         // Forward navigation: use natural MIDI sustain behavior
            //         const sustainState = getSustainStateAtTime(targetTime);
            //         midiEventMerger.updateFileSustainState(sustainState);
            //         const finalSustainState = midiEventMerger.getFinalSustainState();
            //         onSustainStateChange(finalSustainState);
            //     } else {
            //         // Backward navigation: turn off sustain directly, rely on precomputed events
            //         onSustainStateChange(false);
            //     }
            // }
        }
    }, [parsedMidi, playbackState.isPlaying, playbackState.isScrubbing, playbackState.currentTime, isReadingMidi, isComputingNav, groupedEventTimes, handleScrubMove, getSustainStateAtTime, midiEventMerger, onSustainStateChange, onFileInteraction]);

    // Selecting regions: in the zoomed view, or by << >>. A plain click
    // selects one, shift selects a run from the anchor, ⌘ / Ctrl adds or
    // takes out one (selectionAfterClick). While paused it also toggles on
    // every note of the selected regions together, through the same
    // paused-file path a scrub uses, so the keys and the Chord Symbol panel
    // show what merging them would give. The notes go in silently and are
    // struck as a chart chord is, rolled or strummed, not all at once. The
    // playhead stays where it is. `within` is the regions an edit has just
    // replaced the current ones with.
    const selectRegion = useCallback((
        index: number,
        within: ChordRegion[] = regions,
        modifiers: { shift: boolean; toggle: boolean } = { shift: false, toggle: false },
    ) => {
        const selected = selectionAfterClick(within, selectedRegions, selectionAnchor, index, modifiers);
        setEditingRegion(null);
        setSelectedRegions(selected);
        if (!modifiers.shift) setSelectionAnchor(index);
        if (selected.includes(index)) setClickedRegion(index);
        if (!midiEventMerger || playbackState.isPlaying || playbackState.isScrubbing) return;
        const notes = Array.from(new Set(
            within.filter(r => selected.includes(r.index)).reduce<number[]>((all, r) => all.concat(regionVoicing(r)), [])
        )).sort((a, b) => a - b);
        onFileInteraction();
        clearAllNotes();
        const velocityTrim = getBatchVelocityTrim(notes.length);
        notes.forEach(note => {
            const noteOnData = [144, note, trimVelocity(80, velocityTrim)];
            midiEventMerger.handleFileMidi(noteOnData, true, true, true, true); // restoreOnly: listed, not sounded
            sendToMidiOutput(noteOnData);
        });
        if (notes.length > 0) onPlayChordNotes(notes);
        // The next scrub or note navigation turns these off as notes it left
        scrubbingActiveNotesRef.current = new Set(notes);
        scrubbingActiveEventIdsRef.current = new Set();
    }, [regions, selectedRegions, selectionAnchor, midiEventMerger, playbackState.isPlaying, playbackState.isScrubbing, onFileInteraction, clearAllNotes, sendToMidiOutput, onPlayChordNotes]);

    // Chord region navigation (<< >>): the playhead to the start of the next
    // or previous region, which is then selected, sounding the region's own
    // notes as a click on it does. Not the file's notes at that instant: the
    // chord before may still be dying away there, notes the region's naming
    // rule leaves out. From inside a region, << goes to its own start first.
    const navigateToRegion = useCallback((direction: 'next' | 'prev') => {
        if (!parsedMidi || playbackState.isPlaying || playbackState.isScrubbing || isReadingMidi || isComputingNav) return;
        const currentTime = playbackState.currentTime;
        let k = -1;
        if (direction === 'next') {
            // A region starting right here and not yet selected on its own
            // comes first, so >> from the top of the file plays the first region
            const here = regions.findIndex(r => Math.abs(r.start - currentTime) <= 0.0001);
            const aloneSelected = here >= 0 && selectedRegions.length === 1 && selectedRegions[0] === regions[here].index;
            k = here >= 0 && !aloneSelected
                ? here
                : regions.findIndex(r => r.start > currentTime + 0.0001);
        } else {
            for (let i = regions.length - 1; i >= 0 && k < 0; i--) {
                if (regions[i].start < currentTime - 0.0001) k = i;
            }
        }
        if (k < 0) return;
        const start = regions[k].start;
        previousScrubTimeRef.current = start;
        setPlaybackState(prev => ({ ...prev, currentTime: start }));
        selectRegion(regions[k].index);
    }, [parsedMidi, playbackState.isPlaying, playbackState.isScrubbing, playbackState.currentTime, isReadingMidi, isComputingNav, regions, selectedRegions, selectRegion]);

    // Play/Pause toggle
    const togglePlayback = useCallback(async (from?: number) => {
        if (!parsedMidi || playbackState.isScrubbing || isReadingMidi || isComputingNav) return;
        // One driver: while the chart plays, the file only pauses
        if (playLocked && !playbackState.isPlaying) return;
        setEditingRegion(null);

        onFileInteraction();

        try {
            if (Tone.getContext().state !== 'running') {
                await Tone.start();
            }

            if (playbackState.isPlaying) {
                // Pause - pause transport, stop streaming, and move file notes to toggledNotes
                Tone.getTransport().pause();
                if (streamingPlaybackRef.current) {
                    clearInterval(streamingPlaybackRef.current);
                    streamingPlaybackRef.current = null;
                }

                // Freeze what is sounding into the paused note lists
                if (midiEventMerger) {
                    const pauseTime = playbackState.currentTime;
                    const fileEventsHere = getActiveNoteEventsAtTime(pauseTime);
                    const fileNotesHere = new Set<number>(fileEventsHere.map(event => event.noteNumber));
                    const liveKeysDown = new Set<number>(midiEventMerger.getLiveActiveNotes());
                    const allVisibleNotes = [...new Set([...activeNotes, ...heldNotes])];

                    // A key that is physically down stays ACTIVE. Pausing the file
                    // says nothing about the player's hands, and calling a pressed
                    // key "toggled" would claim the user clicked it in the UI.
                    const stillPressed = allVisibleNotes.filter(note => liveKeysDown.has(note));
                    const notPressed = allVisibleNotes.filter(note => !liveKeysDown.has(note));

                    // Of the rest, the notes the FILE is sounding at this position
                    // become the paused playhead notes - that is what makes them
                    // clickable to toggle off, which is the point of the list.
                    const playheadNotes = notPressed.filter(note => fileNotesHere.has(note));

                    // Anything left had its note-off already, and is still ringing
                    // only because the pedal caught it: the definition of heldNotes,
                    // and where the pedal-up release will find it. Sweeping these
                    // into toggledNotes stranded them - navigation turns notes off
                    // by diffing against scrubbingActiveNotesRef, which never knew
                    // they were on, and nothing releases a stranded toggledNote,
                    // not even lifting the pedal.
                    const pedalCarried = notPressed.filter(note => !fileNotesHere.has(note));

                    setActiveNotes(stillPressed);
                    setToggledNotes(playheadNotes);
                    setHeldNotes(pedalCarried);

                    // Navigation works by diffing the target position against this
                    // tracking state, so it has to start from where the playhead
                    // actually stopped rather than from the last scrub
                    scrubbingActiveNotesRef.current = fileNotesHere;
                    scrubbingActiveEventIdsRef.current = new Set<string>(fileEventsHere.map(event => event.eventId));
                    previousScrubTimeRef.current = pauseTime;

                    // Drop the file's sustain, but a live pedal that is still down
                    // keeps sustain on - the foot did not move just because the
                    // file stopped
                    midiEventMerger.clearFileState();
                    midiEventMerger.updateFileSustainState(false);
                    onSustainStateChange(midiEventMerger.getFinalSustainState());
                }

                setPlaybackState(prev => ({ ...prev, isPlaying: false }));
                onPausedStateChange(true);
            } else {
                // If sound is off, turn it on
                if (!soundOn) {
                    if (!audioInitialized) {
                        await Tone.start();
                        setAudioInitialized(true);
                    }
                    setSoundOn(true);
                }

                // Play from `from` (the zoomed view's cue); otherwise resume
                // here, or from the beginning at the end of the file
                let resumeTime = playbackState.currentTime;
                if (from !== undefined) {
                    resumeTime = from;
                } else if (playbackState.currentTime >= parsedMidi.duration - 0.1) { // 0.1s tolerance
                    resumeTime = 0;
                }

                // Notes left sounding by scrubbing that belong to this moment keep
                // their samples; everything else sounding is released here
                const carryOverNotes = getCarryOverNotes(resumeTime);
                clearAllNotes(carryOverNotes);

                Tone.getTransport().position = resumeTime / playbackState.playbackSpeed;
                setPlaybackState(prev => ({ ...prev, currentTime: resumeTime, isPlaying: true }));
                onPausedStateChange(false);

                // Restore sustain and everything already sounding at this point
                // before the stream starts adding to it (nothing to restore at
                // the beginning of the file)
                seedNotesAtTime(resumeTime, carryOverNotes);

                Tone.getTransport().start();
                // Schedule events after transport is started and positioned correctly
                scheduleEvents();
            }
        } catch (error) {
            console.error('Error toggling playback:', error);
        }
    }, [parsedMidi, playbackState, isReadingMidi, isComputingNav, scheduleEvents, seedNotesAtTime, getCarryOverNotes, onPausedStateChange, soundOn, audioInitialized, setSoundOn, setAudioInitialized, clearAllNotes, midiEventMerger, onSustainStateChange, setPlaybackState, setToggledNotes, setActiveNotes, setHeldNotes, activeNotes, heldNotes, enhancedNotes, onFileInteraction, playLocked]);

    // For the parent: cancelling a file being read, and Shift+Space, which
    // plays and pauses the player whenever a file is loaded. In the zoomed
    // view it plays from the cue, as that view's play button does.
    useImperativeHandle(ref, () => ({
        cancelProcessing: () => {
            processingCancelRef.current = true;
            // Reset all processing states immediately
            setIsReadingMidi(false);
            setIsComputingNav(false);
            setProcessingProgress(0);
            // Clear the loaded MIDI file so UI shows no file loaded
            setParsedMidi(null);
            setEnhancedNotes([]);
            setSustainTimeline([]);
            setGroupedEventTimes([]);
            clearFileAnalysis();
        },
        hasFile: () => parsedMidi !== null,
        togglePlayback: () => { togglePlayback(isZoomed && !playbackState.isPlaying ? cue : undefined); },
        toggleSelectedEnabled: () => toggleSelectedEnabled(),
        isEditingRegion: () => editingRegion !== null,
        stopEditingRegion: () => setEditingRegion(null),
        isPlaying: () => playbackState.isPlaying,
        seekSilently: (seconds: number) => {
            if (!parsedMidi || playbackState.isPlaying || playbackState.isScrubbing) return;
            const time = Math.max(0, Math.min(parsedMidi.duration, seconds));
            previousScrubTimeRef.current = time;
            setPlaybackState(prev => ({ ...prev, currentTime: time }));
            setCue(time);
            // The chord's region is the selection too: one place
            const k = regionAt(regions, time + 0.0005);
            if (k >= 0) {
                setSelectedRegions([regions[k].index]);
                setSelectionAnchor(regions[k].index);
            }
        },
        fileSourceAt: () => {
            if (!fileKey || !parsedMidi) return null;
            const k = regionAt(regions, playbackState.currentTime);
            return k < 0 ? null : { fileKey, fileName: parsedMidi.name || "MIDI file", seconds: regions[k].start };
        },
        // Rebuilt every render: it reads the selection and the regions as they are now
    }));

    // Reset playback - (replaced two stage stop button)
    const resetPlayback = useCallback(() => {
        // onFileInteraction();

        // Set transport to beginning
        Tone.getTransport().position = 0;

        // Clear any stuck notes
        clearAllNotes();

        // Clear file state in merger
        if (midiEventMerger) {
            midiEventMerger.clearFileState();
        }

        // Update currentTime to 0 while preserving playing state
        setPlaybackState(prev => ({
            ...prev,
            currentTime: 0
        }));

        // If still playing, reschedule events from beginning (this handles all the cleanup)
        if (playbackState.isPlaying) {
            scheduleEvents();
        }

    }, [playbackState.isPlaying, midiEventMerger, clearAllNotes, scheduleEvents]);

    // Stop playback - two-stage behavior
    const stopPlayback = useCallback(() => {
        if (playbackState.isPlaying) {
            // First click while playing: just pause (same as play/pause button)
            Tone.getTransport().pause();
            if (streamingPlaybackRef.current) {
                clearInterval(streamingPlaybackRef.current);
                streamingPlaybackRef.current = null;
            }
            setPlaybackState(prev => ({ ...prev, isPlaying: false }));
            onPausedStateChange(true);
        } else {
            // Second click while paused: reset to beginning
            Tone.getTransport().stop();
            Tone.getTransport().position = 0;

            // Clear scheduled events and stop streaming
            scheduledEventsRef.current.forEach(id => Tone.getTransport().clear(id));
            scheduledEventsRef.current = [];

            if (streamingPlaybackRef.current) {
                clearInterval(streamingPlaybackRef.current);
                streamingPlaybackRef.current = null;
            }
            processedEventsSetRef.current.clear(); // Clear processed events

            // Clear scrubbing state
            scrubbingActiveNotesRef.current.clear();
            scrubbingActiveEventIdsRef.current.clear();
            scrubbingSustainStateRef.current = false;
            previousScrubTimeRef.current = 0;

            // Clear file state in merger
            if (midiEventMerger) {
                midiEventMerger.clearFileState();
            }
            // Clear all notes and samples - this handles everything!
            clearAllNotes();

            setPlaybackState(prev => ({
                ...prev,
                isPlaying: false,
                currentTime: 0,
                isScrubbing: false
            }));
            onPausedStateChange(true);
        }
    }, [playbackState.isPlaying, midiEventMerger, clearAllNotes, onPausedStateChange]);

    // Handle playback speed change
    const handleSpeedChange = useCallback((speed: number) => {
        setPlaybackState(prev => ({ ...prev, playbackSpeed: speed }));

        // If playing, reschedule events with new speed
        if (playbackState.isPlaying) {
            const currentSongTime = playbackState.currentTime;

            Tone.getTransport().pause();

            // Set position adjusted for new speed
            Tone.getTransport().position = currentSongTime / speed;

            // Reschedule events with new speed
            scheduleEvents();

            Tone.getTransport().start();
        }
    }, [playbackState.isPlaying, playbackState.currentTime, scheduleEvents]);

    // Progress update effect - don't update during scrubbing - adjust for speed
    useEffect(() => {
        let animationId: number;
        let lastUpdate = 0;
        const targetInterval = 100; // 10fps = 100ms

        const updateProgress = (timestamp: number) => {
            if (playbackState.isPlaying && parsedMidi && !playbackState.isScrubbing) {
                // Only update every 100ms (10fps)
                if (timestamp - lastUpdate >= targetInterval) {
                    const currentTime = Tone.getTransport().seconds * playbackState.playbackSpeed;

                    setPlaybackState(prev => {
                        if (Math.abs(currentTime - prev.currentTime) > 0.05) {
                            return { ...prev, currentTime };
                        }
                        return prev;
                    });

                    // Auto-stop at end
                    if (currentTime >= parsedMidi.duration) {
                        if (playbackState.isLooping) {
                            Tone.getTransport().position = 0;
                        } else {
                            stopPlayback();
                            return;
                        }
                    }

                    lastUpdate = timestamp;
                }

                // Always schedule next frame
                animationId = requestAnimationFrame(updateProgress);
            }
        };

        if (playbackState.isPlaying && parsedMidi && !playbackState.isScrubbing) {
            animationId = requestAnimationFrame(updateProgress);
        }

        return () => {
            if (animationId) {
                cancelAnimationFrame(animationId);
            }
        };
    }, [playbackState.isPlaying, playbackState.isLooping, playbackState.isScrubbing, playbackState.playbackSpeed, parsedMidi, stopPlayback]);

    useEffect(() => {
        return () => {
            // Clear scheduled events on unmount
            scheduledEventsRef.current.forEach(id => Tone.getTransport().clear(id));
        };
    }, []);

    // The playback speed menu, with its label in the player's own row and
    // without it in the zoomed view, where room is short
    const renderSpeedMenu = (showLabel: boolean) => (
                            <div style={{ position: 'relative' }}>
                                <div
                                    onClick={() => setShowSpeedMenu(!showSpeedMenu)}
                                    style={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: '0.3dvw',
                                        cursor: 'pointer',
                                        fontSize: '0.8dvw',
                                        color: '#fff',
                                        padding: '0.2dvw 0.4dvw',
                                        borderRadius: '0.2dvw',
                                        backgroundColor: showSpeedMenu ? 'rgba(255, 255, 255, 0.1)' : 'transparent',
                                    }}
                                >
                                    {showLabel && <span style={{ opacity: 0.8 }}>Playback speed:</span>}
                                    <span style={{ fontWeight: 'bold' }}>{playbackState.playbackSpeed.toFixed(1)}x</span>
                                </div>

                                {showSpeedMenu && (
                                    <>
                                        {/* Backdrop to close menu when clicking outside */}
                                        <div
                                            style={{
                                                position: 'fixed',
                                                top: 0,
                                                left: 0,
                                                right: 0,
                                                bottom: 0,
                                                zIndex: 999
                                            }}
                                            onClick={() => setShowSpeedMenu(false)}
                                        />

                                        {/* Speed menu */}
                                        <div style={{
                                            position: 'absolute',
                                            bottom: '100%',
                                            right: 0,
                                            marginBottom: '0.3dvw',
                                            backgroundColor: '#1a1a1a',
                                            border: '0.05dvw solid rgba(255, 255, 255, 0.1)',
                                            borderRadius: '0.3dvw',
                                            padding: '0.3dvw 0',
                                            minWidth: '7dvw',
                                            zIndex: 1000,
                                            boxShadow: '0 0.2dvw 0.5dvw rgba(0, 0, 0, 0.3)',
                                            // fontFamily: 'Trebuchet MS, sans-serif'
                                        }}>
                                            {speedOptions.map(speed => (
                                                <div
                                                    key={speed}
                                                    onClick={() => {
                                                        handleSpeedChange(speed);
                                                        setShowSpeedMenu(false);
                                                    }}
                                                    onMouseEnter={(e) => {
                                                        e.currentTarget.style.backgroundColor = 'rgba(255, 255, 255, 0.05)';
                                                    }}
                                                    onMouseLeave={(e) => {
                                                        e.currentTarget.style.backgroundColor = playbackState.playbackSpeed === speed ? 'rgba(255, 255, 255, 0.1)' : 'transparent';
                                                    }}
                                                    style={{
                                                        padding: '0.4dvw 0.8dvw',
                                                        fontSize: '0.7dvw',
                                                        color: '#fff',
                                                        backgroundColor: playbackState.playbackSpeed === speed ? 'rgba(255, 255, 255, 0.1)' : 'transparent',
                                                        cursor: 'pointer',
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        justifyContent: 'space-between'
                                                    }}
                                                >
                                                    <span style={{ fontWeight: (playbackState.playbackSpeed === speed || Math.abs(speed - 1) < 1e-6) ? 700 : 400, fontSize: Math.abs(speed - 1) < 1e-6 ? '0.9dvw' : '0.8dvw' }}>{`${speed}x`}</span>
                                                    {playbackState.playbackSpeed === speed && (
                                                        <span style={{ marginLeft: '1dvw', opacity: 0.7 }}>✓</span>
                                                    )}
                                                </div>
                                            ))}
                                        </div>
                                    </>
                                )}
                            </div>
    );

    // << and >>, either side of the note navigation buttons and styled like them
    const renderRegionNavButton = (direction: 'next' | 'prev') => {
        const disabled = playbackState.isPlaying || playbackState.isScrubbing || isReadingMidi || isComputingNav || regions.length === 0;
        return (
            <button
                onClick={() => navigateToRegion(direction)}
                disabled={disabled}
                style={{
                    height: '1.8dvw',
                    width: '1.8dvw',
                    fontSize: '1.2dvw',
                    letterSpacing: '-0.25dvw',
                    padding: 0,
                    borderRadius: '0.5dvw',
                    transition: 'all 0.2s ease',
                    cursor: disabled ? 'auto' : 'pointer',
                    minWidth: '1.8dvw',
                    minHeight: '1.8dvw',
                    boxSizing: 'border-box',
                    opacity: disabled ? 0.6 : 1
                }}
                title={direction === 'next' ? 'Next chord region' : 'Previous chord region'}
                onMouseEnter={(e) => {
                    if (!disabled) {
                        e.currentTarget.style.backgroundColor = '#333';
                        e.currentTarget.style.boxShadow = '0.15dvw 0.15dvw 0.4dvw #1a1a1a, -0.15dvw -0.15dvw 0.4dvw #3a3a3a';
                    }
                }}
                onMouseLeave={(e) => {
                    e.currentTarget.style.backgroundColor = '#2a2a2a';
                    e.currentTarget.style.boxShadow = '0.2dvw 0.2dvw 0.5dvw #1a1a1a, -0.2dvw -0.2dvw 0.5dvw #3a3a3a';
                }}
            >{direction === 'next' ? '\u276F\u276F' : '\u276E\u276E'}</button>
        );
    };

    // Format time for display
    const formatTime = (seconds: number) => {
        const mins = Math.floor(seconds / 60);
        const secs = Math.floor(seconds % 60);
        const hundredths = Math.floor((seconds % 1) * 100);
        return `${mins}:${secs.toString().padStart(2, '0')}.${hundredths.toString().padStart(2, '0')}`;
    };

    // A thin pin where each chord region starts, a darker, fainter shade of
    // the progress fill's teal. Drawn above the fill and transparent to the
    // mouse, so the bar scrubs as before.
    const regionPins = useMemo(() => {
        // The player's duration, as the playhead and scrubbing use
        const duration = parsedMidi ? parsedMidi.duration : 0;
        if (!duration) return null;
        return regions.map(region => (
            <div key={region.index} style={{
                position: 'absolute',
                left: `${(region.start / duration) * 100}%`,
                top: 0,
                width: '0.1dvw',
                height: '100%',
                backgroundColor: 'rgba(15, 100, 100, 0.45)',
                pointerEvents: 'none',
                zIndex: 1,
            }} />
        ));
    }, [regions, parsedMidi]);

    // A faint tint per key on the progress bar, in the key's mode colour,
    // under the pins and transparent to the mouse
    const keyTints = useMemo(() => {
        const duration = parsedMidi ? parsedMidi.duration : 0;
        if (!duration) return null;
        return keyLane.filter(run => run.keyCenter !== null).map(run => (
            <div key={`tint-${run.regionIndex}`} style={{
                position: 'absolute',
                left: `${(run.start / duration) * 100}%`,
                width: `${((run.end - run.start) / duration) * 100}%`,
                top: 0,
                height: '100%',
                backgroundColor: run.keyCenter!.mode === 1 ? 'rgba(70, 70, 190, 0.18)' : 'rgba(150, 150, 0, 0.18)',
                pointerEvents: 'none',
                zIndex: 0,
            }} />
        ));
    }, [keyLane, parsedMidi]);

    return (
        <div className="midi-player" style={{
            height: '4dvw',                    // Reduced from 6dvw to 4dvw
            // width: '70dvw',                    // Increased from 40dvw to 60dvw
            padding: '0.3dvw',                 // Reduced padding
            border: '0.05dvw solid rgba(255, 255, 255, 0.3)',
            borderRadius: '0.3dvw',            // Reduced border radius
            // margin: '47.2dvw 0 0.2dvw -21dvw',             // Reduced margin
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
            display: 'flex',
            flexDirection: 'row',
            alignItems: 'center',
            gap: '0.8dvw',                     // Reduced gap
            position: 'absolute',
            top: '52.4dvw', // 51.8dvw
            left: '8.5dvw',
            right: '0.75dvw',                   // the app's right edge, matching the selectors' 0.5dvw on the left
            zIndex: 500
        }}>
            {keyPicker !== null && (
                <KeyCenterPicker
                    value={keyPicker.regionIndex === null || keyPickerInGlobal ? fileGlobalKey : keyPickerLocalKey}
                    usesGlobal={keyPickerUsesGlobal}
                    anchor={keyPicker.anchor}
                    onPick={pickKeyAt}
                    onUseGlobal={keyPickerCanUseGlobal ? useGlobalKeyAt : undefined}
                    onClear={keyPickerCanClear ? clearKeyAt : undefined}
                    onDismiss={() => setKeyPicker(null)}
                />
            )}
            {isZoomed && parsedMidi ? (
                <MidiZoomView
                    duration={parsedMidi.duration}
                    currentTime={playbackState.currentTime}
                    isPlaying={playbackState.isPlaying}
                    isScrubbing={playbackState.isScrubbing}
                    notes={enhancedNotes}
                    regions={regions}
                    sounding={midiAnalysis ? midiAnalysis.sounding : []}
                    notationLegible={notationLegible}
                    chordSymbol={regionSymbol}
                    chordRoot={regionRoot}
                    noteName={regionNoteName}
                    keyLane={keyLane}
                    onKeyLaneClick={(regionIndex, anchor) => setKeyPicker({ regionIndex, anchor })}
                    globalKey={fileGlobalKey}
                    onGlobalKeyClick={(anchor) => setKeyPicker({ regionIndex: null, anchor })}
                    fastKeyAreas={midiAnalysis && midiAnalysis.fast_key_areas ? useFastKeyAreas : null}
                    onToggleFastKeyAreas={toggleFastKeyAreas}
                    keyMarkers={keyMarkers}
                    followOn={followOn}
                    onToggleFollow={onToggleFollow}
                    playLocked={playLocked && !playbackState.isPlaying}
                    onUseChartKey={useChartKeyAt}
                    onReturnChartKey={(marker) => onReturnChartKey(marker.chordId)}
                    globalKeyMarker={divergentChartGlobalKey}
                    onUseChartGlobalKey={() => {
                        if (divergentChartGlobalKey === null) return;
                        recordEditFromChart();
                        setFileGlobalKey(divergentChartGlobalKey.keyCenter);
                    }}
                    onReturnChartGlobalKey={onReturnChartGlobalKey}
                    selectedRegions={selectedRegions}
                    onSelectRegion={(index, modifiers) => selectRegion(index, regions, modifiers)}
                    onRegionsChange={setRegions}
                    onRegionEdit={recordRegionEdit}
                    onBoundaryDrag={handleBoundaryDrag}
                    chartSelectionStart={followOn ? null : chartSelectionSeconds}
                    canMerge={mergeRange !== null}
                    onMergeRegions={mergeSelectedRegions}
                    canSplitRegion={canSplitRegion}
                    onSplitRegion={splitRegionAtPlayhead}
                    editingRegion={editingRegion}
                    canEditRegion={canEditRegion}
                    onToggleRegionEdit={toggleRegionEdit}
                    onEditRegion={startRegionEdit}
                    onUpdateRegionFromChart={updateRegionFromChart}
                    canToggleEnabled={canToggleEnabled}
                    selectionEnabled={selectedInOrder.length === 0 || isEnabled(selectedInOrder[0])}
                    onToggleEnabled={toggleSelectedEnabled}
                    canPromote={canPromote}
                    promoteTitle={promoteTitle}
                    onPromote={promoteSelectedRegions}
                    divergedRegions={divergedRegions}
                    unchartedRegions={unchartedRegions}
                    onPromoteRegion={promoteRegion}
                    onUpdateChartFromRegion={updateChartFromRegion}
                    canUndo={regionHistory.past.length > 0}
                    canRedo={regionHistory.future.length > 0}
                    onUndo={undoRegionEdit}
                    onRedo={redoRegionEdit}
                    onScrubStart={handleViewScrubStart}
                    onTogglePlay={togglePlayback}
                    cue={cue}
                    onCueChange={setCue}
                    timeAndSpeed={(
                        <>
                            <span style={{ fontSize: '0.75dvw', whiteSpace: 'nowrap' }}>{formatTime(playbackState.currentTime)}</span>
                            {renderSpeedMenu(false)}
                        </>
                    )}
                    onExit={() => setIsZoomed(false)}
                />
            ) : (<>
            {/* Left section */}
            <div style={{
                display: 'flex',
                flexDirection: 'row',          // Changed to row from column
                alignItems: 'center',
                gap: '0.5dvw',
            }}>
                <h5 style={{
                    margin: 0,
                    fontSize: '0.9dvw',
                    whiteSpace: 'nowrap'
                }}>MIDI Player</h5>
                <input
                    type="file"
                    accept=".mid,.midi"
                    onChange={handleFileUpload}
                    style={{
                        fontSize: '0.8dvw',
                        width: '6dvw',          // Control input width
                        backgroundColor: 'rgba(255, 255, 255, 0.1)',
                        borderRadius: '0.2dvw',
                        padding: '0.2dvw 0.4dvw',
                        backdropFilter: 'blur(0.3dvw)',
                        border: '0.05dvw solid rgba(255, 255, 255, 0.1)',
                        color: 'rgba(255, 255, 255, 0.8)',
                        cursor: 'pointer',
                        transition: 'all 0.2s ease',
                        height: '1.8dvw',
                        margin: 0,
                        outline: 'none',
                    }}
                />
            </div>

            {/* Drop zone - shown when no MIDI file is loaded */}
            {!parsedMidi && (
                <div
                    onDrop={handleDrop}
                    onDragOver={handleDragOver}
                    onDragLeave={handleDragLeave}
                    style={{
                        flex: 1,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        border: `0.1dvw dashed ${isDragOver ? 'rgba(69, 207, 207, 0.8)' : 'rgba(255, 255, 255, 0.3)'}`,
                        borderRadius: '0.4dvw',
                        backgroundColor: isDragOver ? 'rgba(69, 207, 207, 0.1)' : 'rgba(255, 255, 255, 0.05)',
                        padding: '1dvw',
                        cursor: 'copy',
                        transition: 'all 0.2s ease',
                        fontSize: '0.9dvw',
                        color: isDragOver ? 'rgb(69, 207, 207)' : 'rgba(255, 255, 255, 0.6)',
                        fontWeight: isDragOver ? 'bold' : 'normal',
                        maxHeight: '3dvw'
                    }}
                >
                    Drop MIDI file here
                </div>
            )}

            {/* Center section */}
            {parsedMidi && (
                <div style={{
                    flex: 1,
                    display: 'flex',
                    flexDirection: 'column',
                    gap: '0.2dvw'              // Reduced gap
                }}>
                    {/* File Info and Controls in one row */}
                    <div style={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        fontSize: '0.8dvw',     // Reduced font size
                        gap: '0.5dvw'
                    }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5dvw' }}>
                            <strong>{parsedMidi.name}</strong>
                            {playbackState.isScrubbing && (
                                <span style={{ color: 'rgb(39, 157, 157)' }}>(Scrubbing)</span>
                            )}
                            {isAnalyzingChords && !(isReadingMidi || isComputingNav) && (
                                <span style={{ color: 'rgb(39, 157, 157)', display: 'flex', alignItems: 'center', gap: '0.3dvw' }}>
                                    Finding chords…
                                    <div className="loading-spinner" />
                                </span>
                            )}
                            {analysisError && (
                                <span style={{ color: 'rgb(200, 120, 120)' }} title={analysisError}>No chord regions: {analysisError}</span>
                            )}
                            {(isReadingMidi || isComputingNav) && (
                                <span style={{ color: 'rgb(39, 157, 157)', display: 'flex', alignItems: 'center', gap: '0.3dvw' }}>
                                    Analyzing File…
                                    <div className="loading-spinner" />
                                    {processingProgress > 0 && <span>{processingProgress}%</span>}
                                    <span
                                        onClick={() => {
                                            processingCancelRef.current = true;
                                            setIsReadingMidi(false);
                                            setIsComputingNav(false);
                                            setProcessingProgress(0);
                                            setParsedMidi(null);
                                            setEnhancedNotes([]);
                                            setSustainTimeline([]);
                                            setGroupedEventTimes([]);
                                            clearFileAnalysis();
                                        }}
                                        style={{
                                            cursor: 'pointer',
                                            backgroundColor: 'rgba(164, 51, 51, 0.8)',
                                            color: 'white',
                                            width: '1dvw',
                                            height: '1dvw',
                                            borderRadius: '0.1dvw',
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            fontSize: '0.8dvw',
                                            fontWeight: 'bold'
                                        }}
                                        title="Cancel processing"
                                    >
                                        ×
                                    </span>
                                </span>
                            )}
                            {midiOutputEnabled && (
                                <div style={{ position: 'relative' }}>
                                    <div
                                        onClick={() => setShowMidiDeviceMenu(!showMidiDeviceMenu)}
                                        style={{
                                            color: hasInputOutputConflict() ? '#ff6b6b' : 'rgb(39, 157, 157)',
                                            cursor: 'pointer',
                                            fontSize: '0.8dvw',
                                            padding: '0.2dvw 0.4dvw',
                                            borderRadius: '0.2dvw',
                                            backgroundColor: showMidiDeviceMenu ? 'rgba(39, 157, 157, 0.1)' : 'transparent',
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: '0.3dvw'
                                        }}
                                    >
                                        <span>{hasInputOutputConflict() ? '⚠️' : '→'}</span>
                                        <span>{getDisplayText()}</span>
                                    </div>

                                    {showMidiDeviceMenu && (
                                        <>
                                            {/* Backdrop to close menu when clicking outside */}
                                            <div
                                                style={{
                                                    position: 'fixed',
                                                    top: 0,
                                                    left: 0,
                                                    right: 0,
                                                    bottom: 0,
                                                    zIndex: 999
                                                }}
                                                onClick={() => setShowMidiDeviceMenu(false)}
                                            />

                                            {/* Unified MIDI Device menu */}
                                            <div style={{
                                                position: 'absolute',
                                                bottom: '100%',
                                                left: 0,
                                                marginBottom: '0.3dvw',
                                                backgroundColor: '#1a1a1a',
                                                border: '0.05dvw solid rgba(255, 255, 255, 0.1)',
                                                borderRadius: '0.3dvw',
                                                padding: '0.3dvw 0',
                                                minWidth: '16dvw',
                                                zIndex: 1000,
                                                boxShadow: '0 0.2dvw 0.5dvw rgba(0, 0, 0, 0.3)',
                                            }}>
                                                {/* Input Devices Section */}
                                                <div style={{
                                                    padding: '0.4dvw 0.8dvw',
                                                    fontSize: '0.6dvw',
                                                    color: 'rgba(255, 255, 255, 0.7)',
                                                    fontWeight: 'bold',
                                                    textTransform: 'uppercase',
                                                    letterSpacing: '0.05em'
                                                }}>
                                                    Input Devices
                                                </div>

                                                {/* All MIDI devices option */}
                                                <div
                                                    onClick={() => {
                                                        setSelectedInputId("");
                                                    }}
                                                    onMouseEnter={(e) => {
                                                        if (!midiOutputEnabled || selectedOutputId === "no-device") {
                                                            e.currentTarget.style.backgroundColor = 'rgba(255, 255, 255, 0.05)';
                                                        }
                                                    }}
                                                    onMouseLeave={(e) => {
                                                        e.currentTarget.style.backgroundColor = (selectedInputId === "" || selectedInputId === null) ? 'rgba(255, 255, 255, 0.1)' : 'transparent';
                                                    }}
                                                    style={{
                                                        padding: '0.4dvw 0.8dvw',
                                                        fontSize: '0.7dvw',
                                                        color: (midiOutputEnabled && selectedOutputId !== "no-device") ? '#666' : '#fff',
                                                        backgroundColor: (selectedInputId === "" || selectedInputId === null) ? 'rgba(255, 255, 255, 0.1)' : 'transparent',
                                                        cursor: (midiOutputEnabled && selectedOutputId !== "no-device") ? 'not-allowed' : 'pointer',
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        justifyContent: 'space-between'
                                                    }}
                                                >
                                                    <span>All MIDI devices</span>
                                                    {(selectedInputId === "" || selectedInputId === null) && <span>✓</span>}
                                                </div>

                                                {/* No device option */}
                                                <div
                                                    onClick={() => {
                                                        setSelectedInputId("no-device");
                                                    }}
                                                    onMouseEnter={(e) => {
                                                        e.currentTarget.style.backgroundColor = 'rgba(255, 255, 255, 0.05)';
                                                    }}
                                                    onMouseLeave={(e) => {
                                                        e.currentTarget.style.backgroundColor = selectedInputId === "no-device" ? 'rgba(255, 255, 255, 0.1)' : 'transparent';
                                                    }}
                                                    style={{
                                                        padding: '0.4dvw 0.8dvw',
                                                        fontSize: '0.7dvw',
                                                        color: '#fff',
                                                        backgroundColor: selectedInputId === "no-device" ? 'rgba(255, 255, 255, 0.1)' : 'transparent',
                                                        cursor: 'pointer',
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        justifyContent: 'space-between'
                                                    }}
                                                >
                                                    <span>No input device</span>
                                                    {selectedInputId === "no-device" && <span>✓</span>}
                                                </div>

                                                {/* Individual input devices */}
                                                {midiAccess && Array.from(midiAccess.inputs.values()).map(input => (
                                                    <div
                                                        key={input.id}
                                                        onClick={() => {
                                                            // Check for conflicts before allowing selection
                                                            const isConflicted = midiOutputs.some(output => output.name === input.name && output.id === selectedOutputId);

                                                            // Only allow selection if not conflicted
                                                            if (!isConflicted) {
                                                                setSelectedInputId(input.id);
                                                            }
                                                        }}
                                                        onMouseEnter={(e) => {
                                                            const isConflicted = midiOutputs.some(output => output.name === input.name && output.id === selectedOutputId);
                                                            if (!isConflicted) {
                                                                e.currentTarget.style.backgroundColor = 'rgba(255, 255, 255, 0.05)';
                                                            }
                                                        }}
                                                        onMouseLeave={(e) => {
                                                            e.currentTarget.style.backgroundColor = selectedInputId === input.id ? 'rgba(255, 255, 255, 0.1)' : 'transparent';
                                                        }}
                                                        style={{
                                                            padding: '0.4dvw 0.8dvw',
                                                            fontSize: '0.7dvw',
                                                            color: (() => {
                                                                const isConflicted = midiOutputs.some(output => output.name === input.name && output.id === selectedOutputId);
                                                                return isConflicted ? '#666' : '#fff';
                                                            })(),
                                                            backgroundColor: selectedInputId === input.id ? 'rgba(255, 255, 255, 0.1)' : 'transparent',
                                                            cursor: (() => {
                                                                const isConflicted = midiOutputs.some(output => output.name === input.name && output.id === selectedOutputId);
                                                                return isConflicted ? 'not-allowed' : 'pointer';
                                                            })(),
                                                            display: 'flex',
                                                            alignItems: 'center',
                                                            justifyContent: 'space-between'
                                                        }}
                                                    >
                                                        <span>{input.name}</span>
                                                        {(selectedInputId === input.id || selectedInputId === "") && <span>✓</span>}
                                                    </div>
                                                ))}

                                                {/* Separator */}
                                                <div style={{
                                                    height: '0.05dvw',
                                                    backgroundColor: 'rgba(255, 255, 255, 0.1)',
                                                    margin: '0.3dvw 0'
                                                }} />

                                                {/* Output Devices Section */}
                                                <div style={{
                                                    padding: '0.4dvw 0.8dvw',
                                                    fontSize: '0.6dvw',
                                                    color: 'rgba(255, 255, 255, 0.7)',
                                                    fontWeight: 'bold',
                                                    textTransform: 'uppercase',
                                                    letterSpacing: '0.05em'
                                                }}>
                                                    Output Devices
                                                </div>

                                                {/* No output device */}
                                                <div
                                                    onClick={() => {
                                                        setSelectedOutputId("no-device");
                                                    }}
                                                    onMouseEnter={(e) => {
                                                        e.currentTarget.style.backgroundColor = 'rgba(255, 255, 255, 0.05)';
                                                    }}
                                                    onMouseLeave={(e) => {
                                                        e.currentTarget.style.backgroundColor = selectedOutputId === null ? 'rgba(255, 255, 255, 0.1)' : 'transparent';
                                                    }}
                                                    style={{
                                                        padding: '0.4dvw 0.8dvw',
                                                        fontSize: '0.7dvw',
                                                        color: '#fff',
                                                        backgroundColor: selectedOutputId === null ? 'rgba(255, 255, 255, 0.1)' : 'transparent',
                                                        cursor: 'pointer',
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        justifyContent: 'space-between'
                                                    }}
                                                >
                                                    <span>No output device</span>
                                                    {selectedOutputId === "no-device" && <span>✓</span>}
                                                </div>

                                                {/* Individual output devices */}
                                                {midiOutputs.map(output => (
                                                    <div
                                                        key={output.id}
                                                        onClick={() => {
                                                            // Check for conflicts before allowing selection
                                                            const selectedInput = midiAccess && selectedInputId ? Array.from(midiAccess.inputs.values()).find(input => input.id === selectedInputId) : null;
                                                            const isConflicted = (selectedInputId === "" || selectedInputId === null) ||
                                                                (selectedInput && selectedInput.name === output.name);

                                                            // Only allow selection if not conflicted
                                                            if (!isConflicted) {
                                                                setSelectedOutputId(output.id);
                                                            }
                                                        }}
                                                        onMouseEnter={(e) => {
                                                            // Check for conflicts: same device name or "All devices" selected
                                                            const selectedInput = midiAccess && selectedInputId ? Array.from(midiAccess.inputs.values()).find(input => input.id === selectedInputId) : null;
                                                            const isConflicted = (selectedInputId === "" || selectedInputId === null) ||
                                                                (selectedInput && selectedInput.name === output.name);
                                                            if (!isConflicted) {
                                                                e.currentTarget.style.backgroundColor = 'rgba(255, 255, 255, 0.05)';
                                                            }
                                                        }}
                                                        onMouseLeave={(e) => {
                                                            e.currentTarget.style.backgroundColor = selectedOutputId === output.id ? 'rgba(255, 255, 255, 0.1)' : 'transparent';
                                                        }}
                                                        style={{
                                                            padding: '0.4dvw 0.8dvw',
                                                            fontSize: '0.7dvw',
                                                            color: (() => {
                                                                // Check for conflicts: same device name or "All devices" selected
                                                                const selectedInput = midiAccess && selectedInputId ? Array.from(midiAccess.inputs.values()).find(input => input.id === selectedInputId) : null;
                                                                const isConflicted = (selectedInputId === "" || selectedInputId === null) ||
                                                                    (selectedInput && selectedInput.name === output.name);
                                                                return isConflicted ? '#666' : '#fff';
                                                            })(),
                                                            backgroundColor: selectedOutputId === output.id ? 'rgba(255, 255, 255, 0.1)' : 'transparent',
                                                            cursor: (() => {
                                                                // Check for conflicts: same device name or "All devices" selected
                                                                const selectedInput = midiAccess && selectedInputId ? Array.from(midiAccess.inputs.values()).find(input => input.id === selectedInputId) : null;
                                                                const isConflicted = (selectedInputId === "" || selectedInputId === null) ||
                                                                    (selectedInput && selectedInput.name === output.name);
                                                                return isConflicted ? 'not-allowed' : 'pointer';
                                                            })(),
                                                            display: 'flex',
                                                            alignItems: 'center',
                                                            justifyContent: 'space-between'
                                                        }}
                                                    >
                                                        <span>{output.name}</span>
                                                        {selectedOutputId === output.id && <span>✓</span>}
                                                    </div>
                                                ))}
                                            </div>
                                        </>
                                    )}
                                </div>
                            )}
                        </div>

                        {/* Playback Controls */}
                        <div style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: '0.3dvw'
                        }}>



                            <button
                                onClick={resetPlayback}
                                style={{
                                    height: '1.8dvw',
                                    width: '1.8dvw',
                                    fontSize: '1.2dvw',
                                    padding: 0,
                                    // backgroundColor: '#2a2a2a',
                                    // border: 'none',
                                    borderRadius: '0.5dvw',
                                    // color: '#fff',
                                    transition: 'all 0.2s ease',
                                    cursor: 'pointer',
                                    // boxShadow: '0.2dvw 0.2dvw 0.5dvw #1a1a1a, -0.2dvw -0.2dvw 0.5dvw #3a3a3a',
                                    // outline: 'none',
                                    minWidth: '1.8dvw',
                                    minHeight: '1.8dvw',
                                    boxSizing: 'border-box'
                                }}
                                onMouseEnter={(e) => {
                                    e.currentTarget.style.backgroundColor = '#333';
                                    e.currentTarget.style.boxShadow = '0.15dvw 0.15dvw 0.4dvw #1a1a1a, -0.15dvw -0.15dvw 0.4dvw #3a3a3a';
                                }}
                                onMouseLeave={(e) => {
                                    e.currentTarget.style.backgroundColor = '#2a2a2a';
                                    e.currentTarget.style.boxShadow = '0.2dvw 0.2dvw 0.5dvw #1a1a1a, -0.2dvw -0.2dvw 0.5dvw #3a3a3a';
                                }}
                            // \u23ee
                            >{'\u21e4'}</button>

                            {/* Play/Stop/Loop/MIDI buttons */}
                            <button
                                onClick={() => togglePlayback()}
                                disabled={playbackState.isScrubbing || isReadingMidi || isComputingNav}
                                style={{
                                    fontSize: playbackState.isPlaying ? '1.2dvw' : '1.4dvw',
                                    height: '1.8dvw',
                                    width: '1.8dvw',
                                    padding: 0,
                                    lineHeight: '1.8dvw',
                                    // backgroundColor: playbackState.isPlaying ? '#3a3a3a' : '#2a2a2a',
                                    // border: 'none',
                                    borderRadius: '0.5dvw',
                                    color: playbackState.isPlaying ? '#fff' : '#000',
                                    transition: 'all 0.2s ease',
                                    cursor: 'pointer',
                                    opacity: (playbackState.isScrubbing || isReadingMidi || isComputingNav) ? 0.5 : 1,
                                    textShadow: playbackState.isPlaying ? '0 0 0.7dvw #000, 0 0 0.7dvw #000, 0 0 0.4dvw #000, 0 0 1.8dvw #000, 0 0 2.8dvw #000' : '',
                                    boxShadow: playbackState.isPlaying ? 'inset 0.2dvw 0.2dvw 0.5dvw #1a1a1a, inset -0.2dvw -0.2dvw 0.5dvw #4a4a4a' : '0.2dvw 0.2dvw 0.5dvw #1a1a1a, -0.2dvw -0.2dvw 0.5dvw #3a3a3a',
                                    // outline: 'none',
                                    minWidth: '1.8dvw',
                                    minHeight: '1.8dvw',
                                    boxSizing: 'border-box'
                                }}
                                onMouseEnter={(e) => {
                                    if (!playbackState.isScrubbing && !isReadingMidi && !isComputingNav) {
                                        e.currentTarget.style.backgroundColor = '#333';
                                        e.currentTarget.style.boxShadow = playbackState.isPlaying ? 'inset 0.15dvw 0.15dvw 0.4dvw #1a1a1a, inset -0.15dvw -0.15dvw 0.4dvw #4a4a4a' : '0.15dvw 0.15dvw 0.4dvw #1a1a1a, -0.15dvw -0.15dvw 0.4dvw #3a3a3a';
                                    }
                                }}
                                onMouseLeave={(e) => {
                                    e.currentTarget.style.backgroundColor = playbackState.isPlaying ? '#3a3a3a' : '#2a2a2a';
                                    e.currentTarget.style.boxShadow = playbackState.isPlaying ? 'inset 0.2dvw 0.2dvw 0.5dvw #1a1a1a, inset -0.2dvw -0.2dvw 0.5dvw #4a4a4a' : '0.2dvw 0.2dvw 0.5dvw #1a1a1a, -0.2dvw -0.2dvw 0.5dvw #3a3a3a';
                                }}
                            // >⏯</button> ⏹ ⏯ '\u23ef' : '\u23f5' ⏯  u23f8
                            >{playbackState.isPlaying ? '\u23ef' : '\u23f5'}</button>

                            {renderRegionNavButton('prev')}

                            {/* Navigation buttons */}
                            <button
                                onClick={() => navigateToEvent('prev')}
                                disabled={playbackState.isPlaying || playbackState.isScrubbing || isReadingMidi || isComputingNav}
                                style={{
                                    height: '1.8dvw',
                                    width: '1.8dvw',
                                    fontSize: '1.2dvw',
                                    padding: 0,
                                    // backgroundColor: '#2a2a2a',
                                    // border: 'none',
                                    borderRadius: '0.5dvw',
                                    // color: '#fff',
                                    transition: 'all 0.2s ease',
                                    cursor: playbackState.isPlaying || isReadingMidi || isComputingNav ? 'auto' : 'pointer',
                                    // boxShadow: '0.2dvw 0.2dvw 0.5dvw #1a1a1a, -0.2dvw -0.2dvw 0.5dvw #3a3a3a',
                                    // outline: 'none',
                                    minWidth: '1.8dvw',
                                    minHeight: '1.8dvw',
                                    boxSizing: 'border-box',
                                    opacity: (playbackState.isPlaying || playbackState.isScrubbing || isReadingMidi || isComputingNav) ? 0.6 : 1
                                }}
                                title="Previous note event"
                                onMouseEnter={(e) => {
                                    if (!isPlayingRef.current && !playbackState.isScrubbing && !isReadingMidi && !isComputingNav) {
                                        e.currentTarget.style.backgroundColor = '#333';
                                        e.currentTarget.style.boxShadow = '0.15dvw 0.15dvw 0.4dvw #1a1a1a, -0.15dvw -0.15dvw 0.4dvw #3a3a3a';
                                    }
                                }}
                                onMouseLeave={(e) => {
                                    e.currentTarget.style.backgroundColor = '#2a2a2a';
                                    e.currentTarget.style.boxShadow = '0.2dvw 0.2dvw 0.5dvw #1a1a1a, -0.2dvw -0.2dvw 0.5dvw #3a3a3a';
                                }}
                            // '\u23f4'
                            >{'\u276E'}</button>

                            {/* Next navigation button */}
                            <button
                                onClick={() => navigateToEvent('next')}
                                disabled={playbackState.isPlaying || playbackState.isScrubbing || isReadingMidi || isComputingNav}
                                style={{
                                    height: '1.8dvw',
                                    width: '1.8dvw',
                                    fontSize: '1.2dvw',
                                    padding: 0,
                                    // backgroundColor: '#2a2a2a',
                                    // border: 'none',
                                    borderRadius: '0.5dvw',
                                    // color: '#fff',
                                    transition: 'all 0.2s ease',
                                    cursor: playbackState.isPlaying || isReadingMidi || isComputingNav ? 'auto' : 'pointer',
                                    // boxShadow: '0.2dvw 0.2dvw 0.5dvw #1a1a1a, -0.2dvw -0.2dvw 0.5dvw #3a3a3a',
                                    // outline: 'none',
                                    minWidth: '1.8dvw',
                                    minHeight: '1.8dvw',
                                    boxSizing: 'border-box',
                                    opacity: (playbackState.isPlaying || playbackState.isScrubbing || isReadingMidi || isComputingNav) ? 0.6 : 1
                                }}
                                title="Next note event"
                                onMouseEnter={(e) => {
                                    if (!isPlayingRef.current && !playbackState.isScrubbing && !isReadingMidi && !isComputingNav) {
                                        e.currentTarget.style.backgroundColor = '#333';
                                        e.currentTarget.style.boxShadow = '0.15dvw 0.15dvw 0.4dvw #1a1a1a, -0.15dvw -0.15dvw 0.4dvw #3a3a3a';
                                    }
                                }}
                                onMouseLeave={(e) => {
                                    e.currentTarget.style.backgroundColor = '#2a2a2a';
                                    e.currentTarget.style.boxShadow = '0.2dvw 0.2dvw 0.5dvw #1a1a1a, -0.2dvw -0.2dvw 0.5dvw #3a3a3a';
                                }}
                            // u23f5
                            >{'\u276F'}</button>

                            {renderRegionNavButton('next')}

                            <button
                                onClick={() => setPlaybackState(prev => ({ ...prev, isLooping: !prev.isLooping }))}
                                style={{
                                    height: '1.8dvw',
                                    width: '1.8dvw',
                                    fontSize: playbackState.isLooping ? '1.7dvw' : '1.7dvw',
                                    lineHeight: '1.8dvw',
                                    padding: 0,
                                    // backgroundColor: playbackState.isLooping ? '#3a3a3a' : '#2a2a2a',
                                    // border: 'none',
                                    borderRadius: '0.5dvw',
                                    color: playbackState.isLooping ? '#fff' : '#000',
                                    transition: 'all 0.0s ease',
                                    cursor: 'pointer',
                                    textShadow: playbackState.isLooping ? '0 0 0.7dvw #000, 0 0 0.7dvw #000, 0 0 0.4dvw #000, 0 0 1.8dvw #000, 0 0 2.8dvw #000' : '',
                                    boxShadow: playbackState.isLooping ? 'inset 0.2dvw 0.2dvw 0.5dvw #1a1a1a, inset -0.2dvw -0.2dvw 0.5dvw #4a4a4a' : '0.2dvw 0.2dvw 0.5dvw #1a1a1a, -0.2dvw -0.2dvw 0.5dvw #3a3a3a',
                                    // outline: 'none',
                                    minWidth: '1.8dvw',
                                    minHeight: '1.8dvw',
                                    boxSizing: 'border-box'
                                }}
                                title="Loop Playback"
                                onMouseEnter={(e) => {
                                    e.currentTarget.style.backgroundColor = '#333';
                                    e.currentTarget.style.boxShadow = playbackState.isLooping ? 'inset 0.15dvw 0.15dvw 0.4dvw #1a1a1a, inset -0.15dvw -0.15dvw 0.4dvw #4a4a4a' : '0.15dvw 0.15dvw 0.4dvw #1a1a1a, -0.15dvw -0.15dvw 0.4dvw #3a3a3a';
                                }}
                                onMouseLeave={(e) => {
                                    e.currentTarget.style.backgroundColor = playbackState.isLooping ? '#3a3a3a' : '#2a2a2a';
                                    e.currentTarget.style.boxShadow = playbackState.isLooping ? 'inset 0.2dvw 0.2dvw 0.5dvw #1a1a1a, inset -0.2dvw -0.2dvw 0.5dvw #4a4a4a' : '0.2dvw 0.2dvw 0.5dvw #1a1a1a, -0.2dvw -0.2dvw 0.5dvw #3a3a3a';
                                }}
                            ><span style={{ transform: 'translateY(-0.2dvw)', display: 'inline-block' }}
                            >{'\u27f2'}</span></button>
                            <button
                                onClick={() => setMidiOutputEnabled(!midiOutputEnabled)}
                                style={{
                                    height: '1.8dvw',
                                    width: '1.8dvw',
                                    fontSize: midiOutputEnabled ? '1.1dvw' : '1.1dvw',
                                    padding: 0,
                                    // backgroundColor: midiOutputEnabled ? '#3a3a3a' : '#2a2a2a',
                                    // border: 'none',
                                    borderRadius: '0.5dvw',
                                    color: midiOutputEnabled ? '#fff' : '#000',
                                    // transition: 'all 0.0s ease',
                                    cursor: 'pointer',
                                    textShadow: midiOutputEnabled ? '0 0 0.7dvw #000, 0 0 0.7dvw #000, 0 0 0.4dvw #000, 0 0 1.8dvw #000, 0 0 2.8dvw #000' : '',
                                    boxShadow: midiOutputEnabled ? 'inset 0.2dvw 0.2dvw 0.5dvw #1a1a1a, inset -0.2dvw -0.2dvw 0.5dvw #4a4a4a' : '0.2dvw 0.2dvw 0.5dvw #1a1a1a, -0.2dvw -0.2dvw 0.5dvw #3a3a3a',
                                    // outline: 'none',
                                    minWidth: '1.8dvw',
                                    minHeight: '1.8dvw',
                                    boxSizing: 'border-box'
                                }}
                                title="MIDI Output"
                                onMouseEnter={(e) => {
                                    e.currentTarget.style.backgroundColor = '#333';
                                    e.currentTarget.style.boxShadow = midiOutputEnabled ? 'inset 0.15dvw 0.15dvw 0.4dvw #1a1a1a, inset -0.15dvw -0.15dvw 0.4dvw #4a4a4a' : '0.15dvw 0.15dvw 0.4dvw #1a1a1a, -0.15dvw -0.15dvw 0.4dvw #3a3a3a';
                                }}
                                onMouseLeave={(e) => {
                                    e.currentTarget.style.backgroundColor = midiOutputEnabled ? '#3a3a3a' : '#2a2a2a';
                                    e.currentTarget.style.boxShadow = midiOutputEnabled ? 'inset 0.2dvw 0.2dvw 0.5dvw #1a1a1a, inset -0.2dvw -0.2dvw 0.5dvw #4a4a4a' : '0.2dvw 0.2dvw 0.5dvw #1a1a1a, -0.2dvw -0.2dvw 0.5dvw #3a3a3a';
                                }}
                            >{'\u21c5'}</button>
                            {/* >🎹</button> */}


                            {renderSpeedMenu(true)}
                        </div>
                    </div>
                    {/* Progress Bar */}
                    <div style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '0.5dvw'
                    }}>
                        <FollowToggle on={followOn} onToggle={onToggleFollow} size="0.85dvw" buttonStyle={{
                            height: '1.4dvw',
                            width: '1.6dvw',
                            minWidth: '1.6dvw',
                            minHeight: '1.4dvw',
                            padding: 0,
                            display: 'inline-flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            lineHeight: 1,
                            borderRadius: '0.4dvw',
                            cursor: 'pointer',
                            boxSizing: 'border-box',
                        }} />
                        <button
                            onClick={() => { setCue(playbackState.currentTime); setIsZoomed(true); }}
                            disabled={isReadingMidi || isComputingNav}
                            title="Zoom in: see the notes and edit chord regions"
                            style={{
                                height: '1.4dvw',
                                width: '1.9dvw',
                                // .midi-player button:active sets width auto; the
                                // minimums hold the size while pressed
                                minWidth: '1.9dvw',
                                minHeight: '1.4dvw',
                                padding: 0,
                                fontSize: '1.3dvw',
                                fontWeight: 900,
                                lineHeight: '1.4dvw',
                                borderRadius: '0.4dvw',
                                cursor: 'pointer',
                                boxSizing: 'border-box',
                            }}
                        >{'\u2922'}</button>
                        <span style={{ fontSize: '0.8dvw', whiteSpace: 'nowrap' }}>
                            {formatTime(playbackState.currentTime)} / {formatTime(parsedMidi.duration)}
                        </span>
                        <div
                            ref={progressBarRef}
                            onMouseDown={handleScrubStart}
                            style={{
                                flex: 1,
                                height: '0.8dvw',        // Reduced height
                                backgroundColor: '#ddd',
                                borderRadius: '0.1dvw',
                                cursor: playbackState.isScrubbing ? 'ew-resize' : 'pointer',
                                position: 'relative'
                            }}
                        >
                            {keyTints}
                            {regionPins}
                            <div style={{
                                width: `${(playbackState.currentTime / parsedMidi.duration) * 100}%`,
                                height: '100%',
                                backgroundColor: playbackState.isScrubbing ? 'rgb(110, 110, 110)' : 'rgb(39, 157, 157)',
                                borderRadius: '0.1dvw',
                                transition: playbackState.isScrubbing ? 'none' : 'width 0.1s ease'
                            }} />
                        </div>
                    </div>
                </div>
            )}
            </>)}
        </div>
    );
});

MidiController.displayName = 'MidiController';

export default MidiController;